/**
 * 仕様書 §7「クライアント側の責務」のジオメトリ生成モジュール。
 *
 * brace / plane / stair のパラメータから実際のメッシュ（頂点・法線・UV・
 * インデックス）を生成する純粋関数群。three.js には依存しないので、
 * エディタ以外（テスト、将来のサーバーサイド書き出し等）からも使える。
 */

import { BLOCK_YAW_ZFIGHT_BIAS, type BlockPose } from './block-lattice';
import type {
  BraceArc,
  BraceEntity,
  BraceGeometry,
  BracePathHole,
  ClothBake,
  ClothMesh,
  Entity,
  ObjectEntity,
  ObjectMountAxis,
  PlaneGeometry,
  StairGeometry,
  Vec3,
} from './types';
import { resolveObjectDefinition } from './object-catalog';
import { objectBoundsCenter, objectWorldPoint, type ObjectBounds } from './object-pose';
import { buildRevolveMesh } from './revolve';
import { VIEW_GRID_TILE, localToWorldXZ } from './view-grid';
import {
  MAX_HOLES,
  MAX_PLANE_POINTS,
  MAX_THICKNESS,
  computePlanarNormal,
  computePolygonNormal,
  isPlanar,
} from './validation';
import {
  clipPolygon,
  ensureWinding,
  mergeOverlappingHoles,
  pointInPolygon,
  polygonInsidePolygon,
  polygonSelfIntersects,
  signedArea2,
  triangulatePolygon,
  triangulatePolygonWithHoles,
  type Point2D as HolePoint2D,
} from './holes';

export { triangulatePolygon, triangulatePolygonWithHoles } from './holes';
export type { Point2D } from './holes';

export interface MeshGroup {
  start: number;
  count: number;
  materialIndex: number;
}

export interface MeshData {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
  groups?: MeshGroup[];
  materials?: string[];
}

export function isFillGeometry(geometry: BraceGeometry): boolean {
  return geometry.fill === true;
}

export function isPathGeometry(geometry: BraceGeometry): boolean {
  return geometry.fill !== true;
}

export function isFillBrace(
  entity: Entity | undefined
): entity is BraceEntity & { geometry: BraceGeometry & { fill: true } } {
  return entity !== undefined && entity.type === 'brace' && entity.geometry.fill === true;
}

export function isPathBrace(
  entity: Entity | undefined
): entity is BraceEntity & { geometry: BraceGeometry & { fill?: false } } {
  return entity !== undefined && entity.type === 'brace' && entity.geometry.fill !== true;
}

export function asPathGeometry(geometry: BraceGeometry): BraceGeometry {
  const next = { ...geometry };
  delete next.fill;
  delete next.normal;
  delete next.capHoles;
  delete next.cloth;
  return next;
}

function clothMeshData(mesh: ClothMesh): MeshData {
  return {
    positions: new Float32Array(mesh.positions),
    normals: new Float32Array(mesh.normals),
    uvs: new Float32Array(mesh.uvs),
    indices: new Uint32Array(mesh.indices),
  };
}

function translateClothBake(cloth: ClothBake, delta: Vec3): ClothBake {
  const positions = cloth.mesh.positions;
  const next = new Array<number>(positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    next[i] = positions[i] + delta.x;
    next[i + 1] = positions[i + 1] + delta.y;
    next[i + 2] = positions[i + 2] + delta.z;
  }
  return {
    settings: { ...cloth.settings },
    mesh: {
      positions: next,
      normals: cloth.mesh.normals.slice(),
      uvs: cloth.mesh.uvs.slice(),
      indices: cloth.mesh.indices.slice(),
    },
  };
}

function fillExtrudeVector(normal: Vec3 | undefined, distance: number | undefined): Vec3 | undefined {
  if (distance === undefined || Math.abs(distance) < 1e-9) return undefined;
  const raw = normal ?? { x: 0, y: 1, z: 0 };
  const n = vec.normalize(raw);
  if (vec.length(n) < 1e-9) return undefined;
  return vec.scale(n, distance);
}

function isBraceGeometry(geometry: BraceGeometry | PlaneGeometry): geometry is BraceGeometry {
  return 'start' in geometry && 'end' in geometry;
}

export function asPlaneGeometry(geometry: BraceGeometry | PlaneGeometry): PlaneGeometry {
  return isBraceGeometry(geometry) ? braceAsSlab(geometry) : geometry;
}

export function asFillGeometry(geometry: PlaneGeometry): BraceGeometry {
  const points = geometry.points;
  const result: BraceGeometry = {
    start: points[0],
    end: points[points.length - 1],
    points,
    closed: true,
    fill: true,
    thickness: geometry.thickness,
    rotation: 0,
  };
  if (geometry.normal) result.normal = geometry.normal;
  if (geometry.arcs && geometry.arcs.length > 0) result.arcs = geometry.arcs;
  const extrude = fillExtrudeVector(
    geometry.normal ?? computePolygonNormal(points) ?? undefined,
    geometry.extrude
  );
  if (extrude) result.extrude = extrude;
  if (geometry.holes && geometry.holes.length > 0) result.capHoles = geometry.holes;
  if (geometry.sideHoles && geometry.sideHoles.length > 0) result.holes = geometry.sideHoles;
  return result;
}

export function braceAsSlab(geometry: BraceGeometry): PlaneGeometry {
  const { points } = bracePolyline(geometry);
  const raw = geometry.normal ?? computePolygonNormal(points);
  const n = raw ? vec.normalize(raw) : { x: 0, y: 1, z: 0 };
  const distance = geometry.extrude && vec.length(n) >= 1e-9 ? vec.dot(geometry.extrude, n) : 0;
  const result: PlaneGeometry = {
    points,
    thickness: geometry.thickness,
  };
  if (geometry.normal) result.normal = geometry.normal;
  if (geometry.arcs && geometry.arcs.length > 0) result.arcs = geometry.arcs;
  if (Math.abs(distance) >= 1e-9) result.extrude = distance;
  if (geometry.capHoles && geometry.capHoles.length > 0) result.holes = geometry.capHoles;
  if (geometry.holes && geometry.holes.length > 0) result.sideHoles = geometry.holes;
  return result;
}

/* ------------------------------------------------------------------ */
/* ベクトル演算ヘルパー                                                */
/* ------------------------------------------------------------------ */

export const vec = {
  add: (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }),
  sub: (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }),
  scale: (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s }),
  dot: (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z,
  cross: (a: Vec3, b: Vec3): Vec3 => ({
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  }),
  length: (a: Vec3): number => Math.hypot(a.x, a.y, a.z),
  normalize: (a: Vec3): Vec3 => {
    const len = Math.hypot(a.x, a.y, a.z);
    if (len < 1e-9) return { x: 0, y: 0, z: 0 };
    return { x: a.x / len, y: a.y / len, z: a.z / len };
  },
};

/** 点からレイへの最短距離。点が始点より後ろなら along は負。 */
export function distancePointToRay(
  point: Vec3,
  origin: Vec3,
  direction: Vec3
): { distance: number; along: number } {
  const toPoint = vec.sub(point, origin);
  const along = vec.dot(toPoint, direction);
  const closest = vec.add(origin, vec.scale(direction, along));
  return { distance: vec.length(vec.sub(point, closest)), along };
}

/** メッシュを組み立てるための可変バッファ */
class MeshBuilder {
  private positions: number[] = [];
  private normals: number[] = [];
  private uvs: number[] = [];
  private indices: number[] = [];

  vertex(p: Vec3, n: Vec3, u: number, v: number): number {
    const index = this.positions.length / 3;
    this.positions.push(p.x, p.y, p.z);
    this.normals.push(n.x, n.y, n.z);
    this.uvs.push(u, v);
    return index;
  }

  triangle(a: number, b: number, c: number): void {
    this.indices.push(a, b, c);
  }

  /** 4頂点 (a,b,c,d) を反時計回り順に受け取り、2枚の三角形にする */
  quad(a: number, b: number, c: number, d: number): void {
    this.indices.push(a, b, c, a, c, d);
  }

  build(): MeshData {
    return {
      positions: new Float32Array(this.positions),
      normals: new Float32Array(this.normals),
      uvs: new Float32Array(this.uvs),
      indices: new Uint32Array(this.indices),
    };
  }
}

/* ------------------------------------------------------------------ */
/* block (§4.1)                                                        */
/* ------------------------------------------------------------------ */

const BOX_FACES: { normal: Vec3; uAxis: Vec3; vAxis: Vec3 }[] = [
  { normal: { x: 1, y: 0, z: 0 }, uAxis: { x: 0, y: 0, z: -1 }, vAxis: { x: 0, y: 1, z: 0 } },
  { normal: { x: -1, y: 0, z: 0 }, uAxis: { x: 0, y: 0, z: 1 }, vAxis: { x: 0, y: 1, z: 0 } },
  { normal: { x: 0, y: 1, z: 0 }, uAxis: { x: 1, y: 0, z: 0 }, vAxis: { x: 0, y: 0, z: -1 } },
  { normal: { x: 0, y: -1, z: 0 }, uAxis: { x: 1, y: 0, z: 0 }, vAxis: { x: 0, y: 0, z: 1 } },
  { normal: { x: 0, y: 0, z: 1 }, uAxis: { x: 1, y: 0, z: 0 }, vAxis: { x: 0, y: 1, z: 0 } },
  { normal: { x: 0, y: 0, z: -1 }, uAxis: { x: -1, y: 0, z: 0 }, vAxis: { x: 0, y: 1, z: 0 } },
];

function rotateYaw(point: Vec3, origin: Vec3, yaw: number): Vec3 {
  const local = localToWorldXZ(point.x - origin.x, point.z - origin.z, yaw);
  return { x: origin.x + local.x, y: point.y, z: origin.z + local.z };
}

/**
 * 向き付き直方体のメッシュを生成する。`origin` は中心座標。
 * UV はワールド単位（1 = テクスチャ1タイル）でスケールする。
 */
export function buildBlockMesh(
  geometry: Pick<BlockPose, 'origin' | 'size'> & { yaw?: number; yawClass?: number }
): MeshData {
  const yawClass = geometry.yawClass ?? 0;
  const origin = {
    ...geometry.origin,
    y: geometry.origin.y + yawClass * BLOCK_YAW_ZFIGHT_BIAS,
  };
  const size = geometry.size ?? { x: 1, y: 1, z: 1 };
  const yaw = geometry.yaw ?? 0;
  const builder = new MeshBuilder();

  for (const face of BOX_FACES) {
    const half = {
      x: (Math.abs(face.normal.x) * size.x) / 2,
      y: (Math.abs(face.normal.y) * size.y) / 2,
      z: (Math.abs(face.normal.z) * size.z) / 2,
    };
    const center = rotateYaw(
      vec.add(origin, {
        x: face.normal.x * half.x,
        y: face.normal.y * half.y,
        z: face.normal.z * half.z,
      }),
      origin,
      yaw
    );
    const uLen = Math.abs(face.uAxis.x) * size.x + Math.abs(face.uAxis.y) * size.y + Math.abs(face.uAxis.z) * size.z;
    const vLen = Math.abs(face.vAxis.x) * size.x + Math.abs(face.vAxis.y) * size.y + Math.abs(face.vAxis.z) * size.z;
    const u = rotateYaw(vec.add(origin, vec.scale(face.uAxis, uLen / 2)), origin, yaw);
    const v = rotateYaw(vec.add(origin, vec.scale(face.vAxis, vLen / 2)), origin, yaw);
    const worldU = vec.sub(u, origin);
    const worldV = vec.sub(v, origin);
    const normal = rotateYaw(vec.add(origin, face.normal), origin, yaw);
    const worldNormal = vec.normalize(vec.sub(normal, origin));

    const a = builder.vertex(vec.sub(vec.sub(center, worldU), worldV), worldNormal, 0, 0);
    const b = builder.vertex(vec.sub(vec.add(center, worldU), worldV), worldNormal, uLen, 0);
    const c = builder.vertex(vec.add(vec.add(center, worldU), worldV), worldNormal, uLen, vLen);
    const d = builder.vertex(vec.add(vec.sub(center, worldU), worldV), worldNormal, 0, vLen);
    builder.quad(a, b, c, d);
  }

  return builder.build();
}

/* ------------------------------------------------------------------ */
/* brace (§4.2)                                                        */
/* ------------------------------------------------------------------ */

/**
 * 軸ベクトルに直交する断面基底 (right, up) を求める。
 * `rotationDeg` で軸まわりに断面を回転させる（斜めの梁・手すり用）。
 */
export function braceCrossSectionBasis(
  axis: Vec3,
  rotationDeg: number
): { axis: Vec3; right: Vec3; up: Vec3 } {
  const dir = vec.normalize(axis);
  // 軸と平行にならない参照ベクトルを選ぶ
  const ref = Math.abs(dir.y) > 0.9 ? { x: 0, y: 0, z: 1 } : { x: 0, y: 1, z: 0 };
  let right = vec.normalize(vec.cross(ref, dir));
  let up = vec.normalize(vec.cross(dir, right));

  const theta = (rotationDeg * Math.PI) / 180;
  if (theta !== 0) {
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    const rotatedRight = vec.add(vec.scale(right, cos), vec.scale(up, sin));
    const rotatedUp = vec.add(vec.scale(up, cos), vec.scale(right, -sin));
    right = rotatedRight;
    up = rotatedUp;
  }
  return { axis: dir, right, up };
}

export interface BracePolyline {
  points: Vec3[];
  closed: boolean;
}

export interface BraceSegment {
  start: Vec3;
  end: Vec3;
}

/** 旧 2点データと折れ線データの両方を点列に正規化する。 */
export function bracePolyline(geometry: BraceGeometry): BracePolyline {
  const closed = geometry.closed === true;
  if (geometry.points && geometry.points.length >= 2) {
    return { points: geometry.points, closed };
  }
  return { points: [geometry.start, geometry.end], closed: false };
}

function polylineArcCenter(arcs: BraceArc[] | undefined, segmentIndex: number): Vec3 | undefined {
  return arcs?.find((arc) => arc.segment === segmentIndex)?.center;
}

function braceArcCenter(geometry: BraceGeometry, segmentIndex: number): Vec3 | undefined {
  return polylineArcCenter(geometry.arcs, segmentIndex);
}

function tessellatePolylineSegments(
  points: Vec3[],
  closed: boolean,
  arcs: BraceArc[] | undefined
): { points: Vec3[]; centers: (Vec3 | undefined)[] } {
  const n = points.length;
  const count = closed ? n : n - 1;
  const out: Vec3[] = [];
  const centers: (Vec3 | undefined)[] = [];
  for (let i = 0; i < count; i++) {
    const start = points[i];
    const end = points[(i + 1) % n];
    const center = polylineArcCenter(arcs, i);
    const piece = center ? (braceBendArc(start, end, center) ?? [start, end]) : [start, end];
    for (let k = 0; k < piece.length - 1; k++) centers.push(center);
    if (i === 0) out.push(...piece);
    else out.push(...piece.slice(1));
  }
  if (closed && out.length > 1 && vec.length(vec.sub(out[out.length - 1], out[0])) < 1e-9) {
    out.pop();
  }
  return { points: out, centers };
}

function tessellatePolyline(points: Vec3[], closed: boolean, arcs: BraceArc[] | undefined): Vec3[] {
  return tessellatePolylineSegments(points, closed, arcs).points;
}

/** 閉じた点列を reverse したとき、辺 i に付いていた属性を付け替える。 */
function reverseClosedSegmentCenters<T>(centers: T[]): T[] {
  const n = centers.length;
  return centers.map((_, i) => centers[(2 * n - 2 - i) % n]);
}

function distancePointToSegment(point: Vec3, start: Vec3, end: Vec3): number {
  const ab = vec.sub(end, start);
  const length = vec.length(ab);
  if (length < 1e-9) return vec.length(vec.sub(point, start));
  const t = Math.min(1, Math.max(0, vec.dot(vec.sub(point, start), ab) / (length * length)));
  return vec.length(vec.sub(point, vec.add(start, vec.scale(ab, t))));
}

/** 円弧を約15°で分割した点列。描画・当たり判定用。論理頂点は変えず中心は arcs に残す。 */
export function tessellateBracePolyline(geometry: BraceGeometry): BracePolyline {
  const { points, closed } = bracePolyline(geometry);
  return { points: tessellatePolyline(points, closed, geometry.arcs), closed };
}

/** 折れ線の各辺。円弧は分割した辺。長さ 0 の辺は落とす。 */
export function braceSegments(geometry: BraceGeometry): BraceSegment[] {
  const { points, closed } = tessellateBracePolyline(geometry);
  const segments: BraceSegment[] = [];
  const n = points.length;
  const count = closed ? n : n - 1;
  for (let i = 0; i < count; i++) {
    const start = points[i];
    const end = points[(i + 1) % n];
    if (vec.length(vec.sub(end, start)) < 1e-9) continue;
    segments.push({ start, end });
  }
  return segments;
}

/** 曲げツールの円弧分割（度）。 */
export const BRACE_BEND_STEP_DEG = 15;

function rotateAroundAxis(v: Vec3, axis: Vec3, angle: number): Vec3 {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const crossed = vec.cross(axis, v);
  const along = vec.dot(axis, v);
  return vec.add(vec.add(vec.scale(v, cos), vec.scale(crossed, sin)), vec.scale(axis, along * (1 - cos)));
}

/** hint を AB の垂直二等分面へ下ろす。A≈B、または hint が AB 上なら null。 */
function projectBendHint(a: Vec3, b: Vec3, hint: Vec3): Vec3 | null {
  const ab = vec.sub(b, a);
  const length = vec.length(ab);
  if (length < 1e-9) return null;
  const axis = vec.scale(ab, 1 / length);
  const mid = vec.scale(vec.add(a, b), 0.5);
  const projected = vec.sub(hint, vec.scale(axis, vec.dot(vec.sub(hint, mid), axis)));
  if (vec.length(vec.cross(vec.sub(a, projected), vec.sub(b, projected))) < 1e-8) return null;
  return projected;
}

/**
 * 二点 A,B を通る円の中心。hint は曲がる側の接線制御点（極）。
 * 垂直二等分面へ下ろし、直角二等辺なら 90° 円弧の中心になる。
 * A≈B、または hint が AB 上なら null。
 */
export function braceBendCenter(a: Vec3, b: Vec3, hint: Vec3): Vec3 | null {
  const pole = projectBendHint(a, b, hint);
  if (!pole) return null;
  const mid = vec.scale(vec.add(a, b), 0.5);
  const offset = vec.sub(pole, mid);
  const h2 = vec.dot(offset, offset);
  if (h2 < 1e-16) return null;
  const half = vec.length(vec.sub(b, a)) * 0.5;
  return vec.sub(mid, vec.scale(offset, (half * half) / h2));
}

/**
 * A から B へ、hint を中心にした短い円弧の点列（両端を含む）。
 * 分割は `stepDeg` ごと。円が作れなければ null。
 */
export function braceBendArc(
  a: Vec3,
  b: Vec3,
  hint: Vec3,
  stepDeg = BRACE_BEND_STEP_DEG
): Vec3[] | null {
  const center = projectBendHint(a, b, hint);
  if (!center) return null;
  const from = vec.sub(a, center);
  const to = vec.sub(b, center);
  const cross = vec.cross(from, to);
  const axis = vec.normalize(cross);
  const angle = Math.atan2(vec.length(cross), vec.dot(from, to));
  const step = (Math.max(stepDeg, 1e-3) * Math.PI) / 180;
  const segments = Math.max(1, Math.ceil(angle / step - 1e-9));
  const points: Vec3[] = [{ ...a }];
  for (let i = 1; i < segments; i++) {
    points.push(vec.add(center, rotateAroundAxis(from, axis, (angle * i) / segments)));
  }
  points.push({ ...b });
  return points;
}

/** 円弧上の点の接線。A→B の短い円弧と同じ向き。 */
function arcTangent(point: Vec3, center: Vec3, start: Vec3, end: Vec3): Vec3 | null {
  const axis = vec.normalize(vec.cross(vec.sub(start, center), vec.sub(end, center)));
  if (vec.length(axis) < 1e-9) return null;
  const tangent = vec.cross(axis, vec.sub(point, center));
  const length = vec.length(tangent);
  if (length < 1e-9) return null;
  return vec.scale(tangent, 1 / length);
}

function facingNormal(normal: Vec3, reference: Vec3): Vec3 {
  if (vec.length(normal) < 1e-9) return reference;
  return vec.dot(normal, reference) < 0 ? vec.scale(normal, -1) : normal;
}

function nearestPolylineSegmentIndex(
  points: Vec3[],
  closed: boolean,
  arcs: BraceArc[] | undefined,
  point: Vec3
): number {
  const n = points.length;
  const count = closed ? n : n - 1;
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < count; i++) {
    const start = points[i];
    const end = points[(i + 1) % n];
    const center = polylineArcCenter(arcs, i);
    const piece = center ? (braceBendArc(start, end, center) ?? [start, end]) : [start, end];
    for (let j = 0; j < piece.length - 1; j++) {
      const dist = distancePointToSegment(point, piece[j], piece[j + 1]);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
  }
  return best;
}

/** 点に一番近い論理辺の始点 index。円弧は分割した辺で測る。無ければ -1。 */
export function nearestBraceSegmentIndex(geometry: BraceGeometry, point: Vec3): number {
  const { points, closed } = bracePolyline(geometry);
  return nearestPolylineSegmentIndex(points, closed, geometry.arcs, point);
}

/** 折れ線の1論理辺だけを取り出した geometry。円弧なら中心も segment 0 に付け替える。 */
export function braceLogicalSegment(
  geometry: BraceGeometry,
  segmentIndex: number
): BraceGeometry | null {
  const { points, closed } = bracePolyline(geometry);
  const n = points.length;
  const count = closed ? n : n - 1;
  if (segmentIndex < 0 || segmentIndex >= count) return null;
  const start = points[segmentIndex];
  const end = points[(segmentIndex + 1) % n];
  const center = braceArcCenter(geometry, segmentIndex);
  const result: BraceGeometry = {
    start,
    end,
    thickness: geometry.thickness,
    rotation: geometry.rotation,
  };
  if (center) result.arcs = [{ segment: 0, center }];
  return result;
}

function braceGeometryWithArcs(
  geometry: BraceGeometry,
  arcs: BraceGeometry['arcs'],
  closed: boolean
): BraceGeometry {
  const result: BraceGeometry = {
    start: geometry.start,
    end: geometry.end,
    thickness: geometry.thickness,
    rotation: geometry.rotation,
  };
  if (geometry.fill) result.fill = true;
  if (geometry.normal) result.normal = geometry.normal;
  if (geometry.points) result.points = geometry.points;
  if (closed) result.closed = true;
  if (arcs && arcs.length > 0) result.arcs = arcs;
  const extrude = braceExtrudeOffset(geometry);
  if (vec.length(extrude) >= 1e-9) result.extrude = extrude;
  if (geometry.capHoles && geometry.capHoles.length > 0) {
    result.capHoles = geometry.capHoles.map((hole) => hole.map((p) => ({ ...p })));
  }
  if (geometry.holes && geometry.holes.length > 0) {
    result.holes = geometry.holes.map((hole) => ({
      segment: hole.segment,
      uv: hole.uv.map((p) => ({ u: p.u, v: p.v })),
    }));
  }
  if (geometry.revolve) result.revolve = { axis: geometry.revolve.axis };
  return result;
}

function cloneBraceGeometry(
  geometry: BraceGeometry,
  overrides: { arcs?: BraceArc[]; extrude?: Vec3 | null } = {}
): BraceGeometry {
  const { closed } = bracePolyline(geometry);
  const result = braceGeometryWithArcs(
    geometry,
    overrides.arcs !== undefined ? overrides.arcs : geometry.arcs,
    closed
  );
  if (overrides.extrude !== undefined) {
    if (overrides.extrude && vec.length(overrides.extrude) >= 1e-9) {
      result.extrude = overrides.extrude;
    } else {
      delete result.extrude;
    }
  }
  if (geometry.holes && geometry.holes.length > 0) {
    result.holes = geometry.holes.map((hole) => ({
      segment: hole.segment,
      uv: hole.uv.map((p) => ({ u: p.u, v: p.v })),
    }));
  }
  return result;
}

/** 押し出しオフセット。省略またはほぼ 0 はゼロベクトル。 */
export function braceExtrudeOffset(geometry: BraceGeometry): Vec3 {
  const offset = geometry.extrude;
  if (!offset) return { x: 0, y: 0, z: 0 };
  return vec.length(offset) < 1e-9 ? { x: 0, y: 0, z: 0 } : offset;
}

/** 点に一番近い論理頂点。押し出し中は元の頂点とずらした頂点の近い方で測る。無ければ -1。 */
export function nearestBraceVertexIndex(geometry: BraceGeometry, point: Vec3): number {
  const { points } = bracePolyline(geometry);
  if (points.length === 0) return -1;
  const offset = braceExtrudeOffset(geometry);
  const shifted = vec.length(offset) >= 1e-9;
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < points.length; i++) {
    const vertex = points[i];
    let dist = vec.length(vec.sub(point, vertex));
    if (shifted) {
      dist = Math.min(dist, vec.length(vec.sub(point, vec.add(vertex, offset))));
    }
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

export function braceVertexAt(geometry: BraceGeometry, index: number): Vec3 | null {
  const { points } = bracePolyline(geometry);
  return points[index] ?? null;
}

export interface BraceStripQuad {
  a: Vec3;
  b: Vec3;
  c: Vec3;
  d: Vec3;
  /** この分割辺が円弧なら、その中心。法線を円の接線から出す。 */
  center?: Vec3;
}

function quadArea(a: Vec3, b: Vec3, offset: Vec3): number {
  return vec.length(vec.cross(vec.sub(b, a), offset));
}

/**
 * 押し出した帯の四辺形。円弧は分割したあと、各辺を同じオフセットで掃引する。
 * 面積 0 の辺は落とす。
 */
export function braceStripQuads(geometry: BraceGeometry): BraceStripQuad[] {
  const offset = braceExtrudeOffset(geometry);
  if (vec.length(offset) < 1e-9) return [];
  const { points, closed } = bracePolyline(geometry);
  const tessellated = tessellatePolylineSegments(points, closed, geometry.arcs);
  const n = tessellated.points.length;
  const count = closed ? n : n - 1;
  const quads: BraceStripQuad[] = [];
  for (let i = 0; i < count; i++) {
    const a = tessellated.points[i];
    const b = tessellated.points[(i + 1) % n];
    if (quadArea(a, b, offset) < 1e-10) continue;
    const quad: BraceStripQuad = { a, b, c: vec.add(b, offset), d: vec.add(a, offset) };
    if (tessellated.centers[i]) quad.center = tessellated.centers[i];
    quads.push(quad);
  }
  return quads;
}

/**
 * hint − origin を押し出しベクトルにする。
 * ほぼ 0 なら外す。もともと無ければ null。長さ超過や全面積 0 も null。
 */
export function setBraceExtrude(geometry: BraceGeometry, origin: Vec3, hint: Vec3): BraceGeometry | null {
  const offset = vec.sub(hint, origin);
  const length = vec.length(offset);
  const had = vec.length(braceExtrudeOffset(geometry)) >= 1e-9;
  if (length < PLANE_EXTRUDE_CLEAR_EPS) {
    if (!had) return null;
    return cloneBraceGeometry(geometry, { extrude: null });
  }
  if (length > MAX_THICKNESS) return null;
  const next = cloneBraceGeometry(geometry, { extrude: offset });
  if (braceStripQuads(next).length === 0) return null;
  return next;
}

/**
 * 論理辺を円弧にする。頂点は増やさず、射影した中心を `arcs` に残す。
 * hint が辺の直線上なら、その辺の円弧を外して直線に戻す。もともと直線なら null。
 */
export function setBraceSegmentArc(
  geometry: BraceGeometry,
  segmentIndex: number,
  hint: Vec3
): BraceGeometry | null {
  const { points, closed } = bracePolyline(geometry);
  const n = points.length;
  const count = closed ? n : n - 1;
  if (segmentIndex < 0 || segmentIndex >= count) return null;
  const center = braceBendCenter(points[segmentIndex], points[(segmentIndex + 1) % n], hint);
  const arcs = (geometry.arcs ?? []).filter((arc) => arc.segment !== segmentIndex);
  if (!center) {
    if (arcs.length === (geometry.arcs ?? []).length) return null;
    return braceGeometryWithArcs(geometry, arcs, closed);
  }

  arcs.push({ segment: segmentIndex, center });
  arcs.sort((a, b) => a.segment - b.segment);
  return braceGeometryWithArcs(geometry, arcs, closed);
}

/**
 * プレーン上の円の中心。hint は曲がる側の制御点で、多角形平面へ下ろしてから垂直二等分面へ射影する。
 * 有効な中心は辺の垂直二等分線（面上）に乗る。
 */
export function planeBendCenter(a: Vec3, b: Vec3, hint: Vec3, normal: Vec3): Vec3 | null {
  const n = vec.normalize(normal);
  if (vec.length(n) < 1e-9) return null;
  const planarHint = vec.sub(hint, vec.scale(n, vec.dot(vec.sub(hint, a), n)));
  return braceBendCenter(a, b, planarHint);
}

/** 円弧を約15°で分割した閉じた輪郭。論理頂点は変えず中心は arcs に残す。 */
export function tessellatePlanePolygon(geometry: PlaneGeometry): Vec3[] {
  return tessellatePolyline(geometry.points, true, geometry.arcs);
}

/** 点に一番近いプレーンの論理辺。円弧は分割した辺で測る。無ければ -1。 */
export function nearestPlaneSegmentIndex(geometry: BraceGeometry | PlaneGeometry, point: Vec3): number {
  const plane = asPlaneGeometry(geometry);
  return nearestPolylineSegmentIndex(plane.points, true, plane.arcs, point);
}

/** プレーンの1論理辺を、ハイライト用の短いブレースにする。 */
export function planeLogicalSegment(
  geometry: PlaneGeometry,
  segmentIndex: number
): BraceGeometry | null {
  const { points } = geometry;
  const n = points.length;
  if (segmentIndex < 0 || segmentIndex >= n) return null;
  const start = points[segmentIndex];
  const end = points[(segmentIndex + 1) % n];
  const center = polylineArcCenter(geometry.arcs, segmentIndex);
  const thickness = Math.max(effectivePlaneThickness(geometry.thickness), 0.04);
  const result: BraceGeometry = {
    start,
    end,
    thickness,
    rotation: 0,
  };
  if (center) result.arcs = [{ segment: 0, center }];
  return result;
}

function clonePlaneGeometry(
  geometry: PlaneGeometry,
  overrides: {
    arcs?: BraceArc[];
    extrude?: number | null;
    holes?: Vec3[][] | null;
    sideHoles?: BracePathHole[] | null;
  } = {}
): PlaneGeometry {
  const result: PlaneGeometry = {
    points: geometry.points,
    thickness: geometry.thickness,
  };
  if (geometry.normal) result.normal = geometry.normal;
  const arcs = overrides.arcs !== undefined ? overrides.arcs : geometry.arcs;
  if (arcs && arcs.length > 0) result.arcs = arcs;
  const extrude = overrides.extrude !== undefined ? overrides.extrude : geometry.extrude;
  if (extrude !== undefined && extrude !== null && Math.abs(extrude) >= 1e-9) {
    result.extrude = extrude;
  }
  const holes = overrides.holes !== undefined ? overrides.holes : geometry.holes;
  if (holes && holes.length > 0) {
    result.holes = holes.map((hole) => hole.map((p) => ({ ...p })));
  }
  const sideHoles = overrides.sideHoles !== undefined ? overrides.sideHoles : geometry.sideHoles;
  if (sideHoles && sideHoles.length > 0) {
    result.sideHoles = sideHoles.map((hole) => ({
      segment: hole.segment,
      uv: hole.uv.map((p) => ({ u: p.u, v: p.v })),
    }));
  }
  return result;
}

/**
 * プレーンの論理辺を円弧にする。頂点は増やさず、面上の中心を `arcs` に残す。
 * hint が辺の直線上なら、その辺の円弧を外して直線に戻す。もともと直線なら null。
 */
export function setPlaneSegmentArc(
  geometry: BraceGeometry | PlaneGeometry,
  segmentIndex: number,
  hint: Vec3
): PlaneGeometry | null {
  const plane = asPlaneGeometry(geometry);
  const { points } = plane;
  const n = points.length;
  if (segmentIndex < 0 || segmentIndex >= n) return null;
  const rawNormal = plane.normal ?? computePolygonNormal(points);
  if (!rawNormal) return null;
  const center = planeBendCenter(points[segmentIndex], points[(segmentIndex + 1) % n], hint, rawNormal);
  const arcs = (plane.arcs ?? []).filter((arc) => arc.segment !== segmentIndex);
  if (!center) {
    if (arcs.length === (plane.arcs ?? []).length) return null;
    return clonePlaneGeometry(plane, { arcs });
  }

  arcs.push({ segment: segmentIndex, center });
  arcs.sort((a, b) => a.segment - b.segment);
  return clonePlaneGeometry(plane, { arcs });
}

/** 押し出しを外す判定。1/2 マススナップの半歩。 */
export const PLANE_EXTRUDE_CLEAR_EPS = VIEW_GRID_TILE / 4;

export function planeExtrudeDistance(geometry: PlaneGeometry): number {
  const distance = geometry.extrude ?? 0;
  return Math.abs(distance) < 1e-9 ? 0 : distance;
}

export interface PlaneSlab {
  /** 始面。押し出し中は輪郭、未押し出しは中面から −normal·t/2。 */
  start: Vec3[];
  /** 始面から終面へのベクトル。 */
  offset: Vec3;
  normal: Vec3;
  paperThin: boolean;
}

/**
 * プレーンの角柱。押し出し中は厚みを使わず、輪郭を始面にして `extrude` だけ伸ばす。
 */
export function planeSlab(geometry: PlaneGeometry): PlaneSlab | null {
  const points = tessellatePlanePolygon(geometry);
  if (points.length < 3) return null;
  const rawNormal = geometry.normal ?? computePolygonNormal(geometry.points);
  if (!rawNormal) return null;
  const normal = vec.normalize(rawNormal);
  if (vec.length(normal) < 1e-9) return null;

  const extrude = planeExtrudeDistance(geometry);
  if (extrude !== 0) {
    return {
      start: points,
      offset: vec.scale(normal, extrude),
      normal,
      paperThin: false,
    };
  }

  const paperThin = geometry.thickness <= 1e-9;
  const thickness = effectivePlaneThickness(geometry.thickness);
  return {
    start: points.map((point) => vec.sub(point, vec.scale(normal, thickness / 2))),
    offset: vec.scale(normal, thickness),
    normal,
    paperThin,
  };
}

/**
 * hint を輪郭面の法線へ下ろした距離を `extrude` にする。
 * ほぼ 0 なら押し出しを外す。もともと無ければ null。
 */
export function setPlaneExtrude(
  geometry: BraceGeometry | PlaneGeometry,
  hint: Vec3
): PlaneGeometry | null {
  const plane = asPlaneGeometry(geometry);
  const rawNormal = plane.normal ?? computePolygonNormal(plane.points);
  if (!rawNormal) return null;
  const normal = vec.normalize(rawNormal);
  if (vec.length(normal) < 1e-9) return null;
  const origin = plane.points[0];
  if (!origin) return null;
  const distance = vec.dot(vec.sub(hint, origin), normal);
  const had = planeExtrudeDistance(plane) !== 0;
  if (Math.abs(distance) < PLANE_EXTRUDE_CLEAR_EPS) {
    if (!had) return null;
    return clonePlaneGeometry(plane, { extrude: null });
  }
  return clonePlaneGeometry(plane, { extrude: distance });
}

/** 上方向押し出しツールの既定1クリック分。スロットの snap で上書きする。 */
export const EXTRUDE_UP_DISTANCE = 1;

function signedToward(normal: Vec3, origin: Vec3, toward: Vec3, view?: Vec3): number {
  const side = vec.dot(vec.sub(toward, origin), normal);
  if (Math.abs(side) >= 1e-6) return Math.sign(side);
  if (view && vec.length(view) >= 1e-9) {
    const facing = -vec.dot(view, normal);
    if (Math.abs(facing) >= 1e-6) return Math.sign(facing);
  }
  return Math.sign(normal.y) || 1;
}

function uniquePlanarNormal(points: Vec3[]): Vec3 | null {
  if (points.length < 3 || !isPlanar(points)) return null;
  const normal = computePlanarNormal(points);
  if (!normal || vec.length(normal) < 1e-9) return null;
  return vec.normalize(normal);
}

function bracePlanarSamplePoints(geometry: BraceGeometry): Vec3[] {
  const { points } = bracePolyline(geometry);
  if (!geometry.arcs || geometry.arcs.length === 0) return points;
  return [...points, ...geometry.arcs.map((arc) => arc.center)];
}

/**
 * 輪郭面から `toward` 側へ法線方向に `distance` メートル足す。
 * 上限超過は null。押し出しがほぼ 0 に戻るときは外す。
 */
export function extrudePlaneUp(
  geometry: BraceGeometry | PlaneGeometry,
  toward: Vec3,
  view?: Vec3,
  distance = EXTRUDE_UP_DISTANCE
): PlaneGeometry | null {
  const plane = asPlaneGeometry(geometry);
  const rawNormal = plane.normal ?? computePolygonNormal(plane.points);
  if (!rawNormal) return null;
  const normal = vec.normalize(rawNormal);
  if (vec.length(normal) < 1e-9) return null;
  const origin = plane.points[0];
  if (!origin) return null;
  const next = planeExtrudeDistance(plane) + signedToward(normal, origin, toward, view) * distance;
  if (Math.abs(next) > MAX_THICKNESS) return null;
  return setPlaneExtrude(plane, vec.add(origin, vec.scale(normal, next)));
}

/**
 * 2点ブレースの上に押し出し軸。上下だけ。鉛直線は面積 0 になるので null。
 */
function twoPointBraceUpAxis(geometry: BraceGeometry, toward: Vec3, view?: Vec3): Vec3 | null {
  const { points } = bracePolyline(geometry);
  if (points.length < 2) return null;
  const origin = points[0];
  const span = vec.sub(points[1], points[0]);
  const spanLen = vec.length(span);
  if (spanLen < 1e-9) return null;
  const dir = vec.scale(span, 1 / spanLen);
  if (Math.abs(dir.y) >= 0.99) return null;
  const up = { x: 0, y: 1, z: 0 };
  let score = toward.y - origin.y;
  if (Math.abs(score) < 1e-6 && view && vec.length(view) >= 1e-9) {
    score = -view.y;
  }
  if (Math.abs(score) < 1e-6) return up;
  return score > 0 ? up : { x: 0, y: -1, z: 0 };
}

/**
 * 同一平面上のブレースを、その面から `toward` 側へ `distance` メートル足す。
 * 2点は上下だけ。折れ線は平面の垂線。
 * 同一平面でなければ null。面積 0 や上限超過も null。
 */
export function extrudeBraceUp(
  geometry: BraceGeometry,
  toward: Vec3,
  view?: Vec3,
  distance = EXTRUDE_UP_DISTANCE
): BraceGeometry | null {
  const polyline = bracePolyline(geometry);
  const origin = polyline.points[0];
  if (!origin) return null;
  const twoPoint = polyline.points.length === 2 && !polyline.closed;
  let axis: Vec3 | null;
  if (twoPoint) {
    axis = twoPointBraceUpAxis(geometry, toward, view);
  } else {
    const normal = uniquePlanarNormal(bracePlanarSamplePoints(geometry));
    if (!normal) return null;
    axis = vec.scale(normal, signedToward(normal, origin, toward, view));
  }
  if (!axis) return null;
  const hint = vec.add(braceExtrudeOffset(geometry), vec.scale(axis, distance));
  return setBraceExtrude(geometry, { x: 0, y: 0, z: 0 }, hint);
}

/**
 * 輪郭面から `toward` と反対側へ法線方向に `distance` メートル足す。
 */
export function extrudePlaneDown(
  geometry: BraceGeometry | PlaneGeometry,
  toward: Vec3,
  view?: Vec3,
  distance = EXTRUDE_UP_DISTANCE
): PlaneGeometry | null {
  return extrudePlaneUp(geometry, toward, view, -distance);
}

/**
 * 同一平面上のブレースを、その面から `toward` と反対側へ `distance` メートル足す。
 */
export function extrudeBraceDown(
  geometry: BraceGeometry,
  toward: Vec3,
  view?: Vec3,
  distance = EXTRUDE_UP_DISTANCE
): BraceGeometry | null {
  return extrudeBraceUp(geometry, toward, view, -distance);
}

/** 輪郭化したときの既定断面。設置スロットの初期値と同じ。 */
export const DEFAULT_BRACE_SECTION = 0.2;

const FILL_EXTRUDE_ALIGN_EPS = 1e-6;

/**
 * 閉じた平面の path を fill にする。太さはそのまま板厚。
 * filled+extruded では押し出しだけが体積で、太さでは太らせない。
 * 押し出しが法線に乗らなければ null。
 */
export function bracePathToFill(geometry: BraceGeometry): BraceGeometry | null {
  if (geometry.holes && geometry.holes.length > 0) return null;
  if (geometry.capHoles && geometry.capHoles.length > 0) return null;
  const { points, closed } = bracePolyline(geometry);
  if (!closed || points.length < 3) return null;
  const normal = uniquePlanarNormal(bracePlanarSamplePoints(geometry));
  if (!normal) return null;
  const offset = braceExtrudeOffset(geometry);
  let extrude: number | undefined;
  if (vec.length(offset) >= 1e-9) {
    const along = vec.dot(offset, normal);
    const residual = vec.sub(offset, vec.scale(normal, along));
    if (vec.length(residual) >= FILL_EXTRUDE_ALIGN_EPS) return null;
    if (Math.abs(along) >= 1e-9) extrude = along;
  }
  return asFillGeometry({
    points,
    thickness: geometry.thickness,
    normal,
    ...(geometry.arcs && geometry.arcs.length > 0 ? { arcs: geometry.arcs } : {}),
    ...(extrude !== undefined ? { extrude } : {}),
  });
}

/**
 * fill を閉じた path にする。断面は fill の太さ。0 なら 0.2。
 * スラブ押し出しがあれば法線方向のベクトルにする。
 */
export function braceFillToPath(geometry: BraceGeometry | PlaneGeometry): BraceGeometry | null {
  const slab = asPlaneGeometry(geometry);
  if (slab.holes && slab.holes.length > 0) return null;
  if (slab.sideHoles && slab.sideHoles.length > 0) return null;
  const points = slab.points;
  const size = slab.thickness > 1e-9 ? slab.thickness : DEFAULT_BRACE_SECTION;
  const result: BraceGeometry = {
    start: points[0],
    end: points[points.length - 1],
    points,
    closed: true,
    thickness: size,
    rotation: 0,
  };
  if (slab.arcs && slab.arcs.length > 0) result.arcs = slab.arcs;
  const distance = planeExtrudeDistance(slab);
  if (distance !== 0) {
    const raw = slab.normal ?? computePolygonNormal(points);
    if (raw) {
      const normal = vec.normalize(raw);
      if (vec.length(normal) >= 1e-9) result.extrude = vec.scale(normal, distance);
    }
  }
  return result;
}

const MITER_LIMIT = 4;

function braceSectionOffsets(right: Vec3, up: Vec3, hw: number, hh: number): Vec3[] {
  return [
    vec.add(vec.scale(right, -hw), vec.scale(up, -hh)),
    vec.add(vec.scale(right, hw), vec.scale(up, -hh)),
    vec.add(vec.scale(right, hw), vec.scale(up, hh)),
    vec.add(vec.scale(right, -hw), vec.scale(up, hh)),
  ];
}

function rotateVecOnto(v: Vec3, from: Vec3, to: Vec3): Vec3 {
  const c = vec.cross(from, to);
  const d = vec.dot(from, to);
  const clen = vec.length(c);
  if (d > 0.999999) return v;
  if (clen < 1e-8) {
    const axis = Math.abs(from.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
    const perp = vec.normalize(vec.cross(from, axis));
    return vec.sub(vec.scale(perp, 2 * vec.dot(v, perp)), v);
  }
  const k = vec.scale(c, 1 / clen);
  const kv = vec.cross(k, v);
  const kdv = vec.dot(k, v);
  return vec.add(vec.add(vec.scale(v, d), vec.scale(kv, clen)), vec.scale(k, kdv * (1 - d)));
}

function transportFrame(
  prev: { axis: Vec3; right: Vec3; up: Vec3 },
  newAxis: Vec3
): { axis: Vec3; right: Vec3; up: Vec3 } {
  let right = rotateVecOnto(prev.right, prev.axis, newAxis);
  right = vec.normalize(vec.sub(right, vec.scale(newAxis, vec.dot(right, newAxis))));
  if (vec.length(right) < 1e-9) {
    return braceCrossSectionBasis(newAxis, 0);
  }
  const up = vec.normalize(vec.cross(newAxis, right));
  return { axis: newAxis, right, up };
}

function miterRing(point: Vec3, offsets: Vec3[], dIn: Vec3, dOut: Vec3): Vec3[] {
  const bisector = vec.add(dIn, dOut);
  const nLen = vec.length(bisector);
  if (nLen < 1e-6) return offsets.map((o) => vec.add(point, o));
  const nMiter = vec.scale(bisector, 1 / nLen);
  const denom = vec.dot(dIn, nMiter);
  if (Math.abs(denom) < 1 / MITER_LIMIT) return offsets.map((o) => vec.add(point, o));
  const inv = Math.max(-MITER_LIMIT, Math.min(MITER_LIMIT, 1 / denom));
  return offsets.map((o) => vec.add(point, vec.add(o, vec.scale(dIn, -vec.dot(o, nMiter) * inv))));
}

/**
 * ブレース（2点を結ぶ四角柱、またはマイター接続した折れ線）のメッシュを生成する。
 * 押し出し中は折れ線を掃引した帯になり、太さは `thickness` の板厚。ジョイントもマイター。
 */
export function buildBraceMesh(geometry: BraceGeometry): MeshData {
  if (vec.length(braceExtrudeOffset(geometry)) >= 1e-9) {
    return buildBraceStripMesh(geometry);
  }
  const { points, closed } = bracePolyline(geometry);
  const tessellated = tessellatePolylineSegments(points, closed, geometry.arcs);
  if (!closed && tessellated.points.length <= 2) {
    return extrudeBraceSegment(geometry.start, geometry.end, geometry);
  }
  return extrudeBracePolyline(tessellated.points, closed, geometry, tessellated.centers);
}

/** 帯の断面。パス→押し出しが幅、面法線が板厚。 */
function stripSectionOffsets(axis: Vec3, offset: Vec3, half: number): Vec3[] | null {
  const normal = vec.normalize(vec.cross(axis, offset));
  if (vec.length(normal) < 1e-9) return null;
  const hn = vec.scale(normal, half);
  return [vec.scale(hn, -1), vec.sub(offset, hn), vec.add(offset, hn), hn];
}

function addStripSide(builder: MeshBuilder, a: Vec3, b: Vec3, c: Vec3, d: Vec3, slab: number): void {
  const side = vec.sub(b, a);
  const face = vec.normalize(vec.cross(side, vec.sub(d, a)));
  if (vec.length(face) < 1e-9) return;
  const span = vec.length(side);
  const p0 = builder.vertex(a, face, 0, 0);
  const p1 = builder.vertex(b, face, span, 0);
  const p2 = builder.vertex(c, face, span, slab);
  const p3 = builder.vertex(d, face, 0, slab);
  builder.quad(p0, p1, p2, p3);
}

function addStripSegment(
  builder: MeshBuilder,
  startRing: Vec3[],
  endRing: Vec3[],
  pathA: Vec3,
  pathB: Vec3,
  offset: Vec3,
  u0: number,
  thickness: number,
  startNormal?: Vec3,
  endNormal?: Vec3
): number {
  const edge = vec.sub(pathB, pathA);
  const normal = vec.normalize(vec.cross(edge, offset));
  if (vec.length(normal) < 1e-9) return u0;
  const n0 = startNormal ? facingNormal(startNormal, normal) : normal;
  const n1 = endNormal ? facingNormal(endNormal, normal) : normal;
  const paperThin = thickness <= 1e-9;
  const slab = effectivePlaneThickness(thickness);
  const u1 = u0 + vec.length(edge);
  const v1 = vec.length(offset);
  const endA = builder.vertex(startRing[3], n0, u0, 0);
  const endB = builder.vertex(endRing[3], n1, u1, 0);
  const endC = builder.vertex(endRing[2], n1, u1, v1);
  const endD = builder.vertex(startRing[2], n0, u0, v1);
  builder.quad(endA, endB, endC, endD);
  const startA = builder.vertex(startRing[0], vec.scale(n0, -1), u0, 0);
  const startB = builder.vertex(endRing[0], vec.scale(n1, -1), u1, 0);
  const startC = builder.vertex(endRing[1], vec.scale(n1, -1), u1, v1);
  const startD = builder.vertex(startRing[1], vec.scale(n0, -1), u0, v1);
  builder.quad(startA, startD, startC, startB);
  if (!paperThin) {
    addStripSide(builder, startRing[0], endRing[0], endRing[3], startRing[3], slab);
    addStripSide(builder, endRing[1], startRing[1], startRing[2], endRing[2], slab);
  }
  return u1;
}

function capStripRing(builder: MeshBuilder, ring: Vec3[], thickness: number, reverse: boolean): void {
  const face = vec.normalize(
    reverse
      ? vec.cross(vec.sub(ring[3], ring[0]), vec.sub(ring[1], ring[0]))
      : vec.cross(vec.sub(ring[1], ring[0]), vec.sub(ring[3], ring[0]))
  );
  if (vec.length(face) < 1e-9) return;
  const span = vec.length(vec.sub(ring[1], ring[0]));
  const idx = [
    builder.vertex(ring[0], face, 0, 0),
    builder.vertex(ring[1], face, span, 0),
    builder.vertex(ring[2], face, span, thickness),
    builder.vertex(ring[3], face, 0, thickness),
  ];
  if (reverse) builder.quad(idx[0], idx[3], idx[2], idx[1]);
  else builder.quad(idx[0], idx[1], idx[2], idx[3]);
}

function lerp3(a: Vec3, b: Vec3, t: number): Vec3 {
  return vec.add(a, vec.scale(vec.sub(b, a), t));
}

/** a(0,0) b(1,0) c(1,1) d(0,1) */
function bilinear3(a: Vec3, b: Vec3, c: Vec3, d: Vec3, u: number, v: number): Vec3 {
  return lerp3(lerp3(a, b, u), lerp3(d, c, u), v);
}

function braceStripRings(
  geometry: BraceGeometry,
  points: Vec3[],
  closed: boolean
): {
  dirs: (Vec3 | null)[];
  rings: (Vec3[] | null)[];
  incoming: (Vec3 | null)[];
  outgoing: (Vec3 | null)[];
} {
  const offset = braceExtrudeOffset(geometry);
  const n = points.length;
  const count = closed ? n : n - 1;
  const half = effectivePlaneThickness(geometry.thickness) / 2;
  const dirs: (Vec3 | null)[] = [];
  for (let i = 0; i < count; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const axisVec = vec.sub(b, a);
    const length = vec.length(axisVec);
    if (length < 1e-9 || quadArea(a, b, offset) < 1e-10) {
      dirs.push(null);
    } else {
      dirs.push(vec.scale(axisVec, 1 / length));
    }
  }

  const rings: (Vec3[] | null)[] = [];
  const incoming: (Vec3 | null)[] = [];
  const outgoing: (Vec3 | null)[] = [];
  for (let i = 0; i < n; i++) {
    const inIndex = closed ? (i - 1 + count) % count : i - 1;
    const outIndex = closed ? i % count : i;
    const dIn = inIndex >= 0 && inIndex < count ? dirs[inIndex] : null;
    const dOut = outIndex >= 0 && outIndex < count ? dirs[outIndex] : null;
    incoming.push(dIn);
    outgoing.push(dOut);
    const axis = dIn ?? dOut;
    if (!axis) {
      rings.push(null);
      continue;
    }
    const section = stripSectionOffsets(axis, offset, half);
    if (!section) {
      rings.push(null);
      continue;
    }
    if (dIn && dOut) {
      rings.push(miterRing(points[i], section, dIn, dOut));
    } else {
      rings.push(section.map((o) => vec.add(points[i], o)));
    }
  }
  return { dirs, rings, incoming, outgoing };
}

/** クロスシミュ用の断面リング。梁は正方形、押し出しは帯。円弧は分割済み。 */
export function braceSectionRings(geometry: BraceGeometry): {
  rings: Vec3[][];
  points: Vec3[];
  closed: boolean;
} | null {
  const { points, closed } = tessellateBracePolyline(geometry);
  const n = points.length;
  if (n < 2 || (closed && n < 3)) return null;

  const offset = braceExtrudeOffset(geometry);
  if (vec.length(offset) >= 1e-9) {
    const { rings } = braceStripRings(geometry, points, closed);
    const out: Vec3[][] = [];
    for (const ring of rings) {
      if (!ring || ring.length < 4) return null;
      out.push(ring);
    }
    return { rings: out, points, closed };
  }

  const count = closed ? n : n - 1;
  const dirs: Vec3[] = [];
  const frames: { axis: Vec3; right: Vec3; up: Vec3 }[] = [];
  for (let i = 0; i < count; i++) {
    const axisVec = vec.sub(points[(i + 1) % n], points[i]);
    const length = vec.length(axisVec);
    if (length < 1e-9) return null;
    const axis = vec.scale(axisVec, 1 / length);
    dirs.push(axis);
    frames.push(
      i === 0
        ? braceCrossSectionBasis(axisVec, geometry.rotation ?? 0)
        : transportFrame(frames[i - 1], axis)
    );
  }

  const hw = geometry.thickness / 2;
  const hh = hw;
  const rings: Vec3[][] = [];
  for (let i = 0; i < n; i++) {
    const incoming = closed || i > 0;
    const outgoing = closed || i < n - 1;
    if (incoming && outgoing) {
      const inIndex = (i - 1 + count) % count;
      const outIndex = closed ? i % count : i;
      const frame = frames[inIndex];
      const offsets = braceSectionOffsets(frame.right, frame.up, hw, hh);
      rings.push(miterRing(points[i], offsets, dirs[inIndex], dirs[outIndex]));
    } else {
      const frame = frames[i === 0 ? 0 : count - 1];
      const offsets = braceSectionOffsets(frame.right, frame.up, hw, hh);
      rings.push(offsets.map((o) => vec.add(points[i], o)));
    }
  }
  return { rings, points, closed };
}

function addHoledStripSegment(
  builder: MeshBuilder,
  startRing: Vec3[],
  endRing: Vec3[],
  pathA: Vec3,
  pathB: Vec3,
  offset: Vec3,
  u0: number,
  thickness: number,
  face: PathFace,
  holes: Vec3[][],
  startNormal?: Vec3,
  endNormal?: Vec3
): number {
  const edge = vec.sub(pathB, pathA);
  const normal = vec.normalize(vec.cross(edge, offset));
  if (vec.length(normal) < 1e-9) {
    return addStripSegment(builder, startRing, endRing, pathA, pathB, offset, u0, thickness, startNormal, endNormal);
  }
  const holesUv = holes.map((ring) => ring.map((p) => {
    const uv = worldToPathUv(face, p);
    return { x: uv.u, y: uv.v };
  }));
  const outer = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
  ];
  const tessellated =
    holesUv.length > 0
      ? triangulatePolygonWithHoles(outer, holesUv)
      : { vertices: outer, indices: triangulatePolygon(outer) };
  if (tessellated.indices.length === 0) {
    return addStripSegment(builder, startRing, endRing, pathA, pathB, offset, u0, thickness, startNormal, endNormal);
  }

  const n0 = startNormal ? facingNormal(startNormal, normal) : normal;
  const n1 = endNormal ? facingNormal(endNormal, normal) : normal;
  const paperThin = thickness <= 1e-9;
  const slab = effectivePlaneThickness(thickness);
  const half = slab / 2;
  const u1 = u0 + vec.length(edge);
  const v1 = vec.length(offset);
  const through = (u: number, v: number, sign: number) =>
    vec.add(pathUvToWorld(face, { u, v }), vec.scale(normal, sign * half));
  const plusBilinear = (u: number, v: number) =>
    bilinear3(startRing[3], endRing[3], endRing[2], startRing[2], u, v);
  const minusBilinear = (u: number, v: number) =>
    bilinear3(startRing[0], endRing[0], endRing[1], startRing[1], u, v);
  const onOuterCorner = (p: { x: number; y: number }) =>
    (Math.abs(p.x) < 1e-9 || Math.abs(p.x - 1) < 1e-9) &&
    (Math.abs(p.y) < 1e-9 || Math.abs(p.y - 1) < 1e-9);
  const plus = (p: { x: number; y: number }) =>
    onOuterCorner(p) ? plusBilinear(p.x, p.y) : through(p.x, p.y, 1);
  const minus = (p: { x: number; y: number }) =>
    onOuterCorner(p) ? minusBilinear(p.x, p.y) : through(p.x, p.y, -1);
  const paperN = (u: number) => vec.normalize(lerp3(n0, n1, u));

  const plusIdx = tessellated.vertices.map((p) =>
    builder.vertex(plus(p), paperN(p.x), u0 + p.x * (u1 - u0), p.y * v1)
  );
  for (let i = 0; i < tessellated.indices.length; i += 3) {
    builder.triangle(plusIdx[tessellated.indices[i]], plusIdx[tessellated.indices[i + 1]], plusIdx[tessellated.indices[i + 2]]);
  }
  const minusIdx = tessellated.vertices.map((p) =>
    builder.vertex(minus(p), vec.scale(paperN(p.x), -1), u0 + p.x * (u1 - u0), p.y * v1)
  );
  for (let i = 0; i < tessellated.indices.length; i += 3) {
    builder.triangle(
      minusIdx[tessellated.indices[i + 2]],
      minusIdx[tessellated.indices[i + 1]],
      minusIdx[tessellated.indices[i]]
    );
  }

  if (!paperThin) {
    addStripSide(builder, startRing[0], endRing[0], endRing[3], startRing[3], slab);
    addStripSide(builder, endRing[1], startRing[1], startRing[2], endRing[2], slab);
    const merged = mergeOverlappingHoles(holesUv).map((ring) => ensureWinding(ring, false));
    for (const ring of merged) {
      for (let i = 0; i < ring.length; i++) {
        const j = (i + 1) % ring.length;
        const a = through(ring[i].x, ring[i].y, -1);
        const b = through(ring[j].x, ring[j].y, -1);
        const c = through(ring[j].x, ring[j].y, 1);
        const d = through(ring[i].x, ring[i].y, 1);
        const wall = vec.sub(b, a);
        const along = vec.sub(d, a);
        const sideNormal = vec.normalize(vec.cross(along, wall));
        if (vec.length(sideNormal) < 1e-9) continue;
        const span = vec.length(wall);
        const p0 = builder.vertex(a, sideNormal, 0, 0);
        const p1 = builder.vertex(b, sideNormal, span, 0);
        const p2 = builder.vertex(c, sideNormal, span, slab);
        const p3 = builder.vertex(d, sideNormal, 0, slab);
        builder.quad(p0, p1, p2, p3);
      }
    }
  }
  return u1;
}

function buildBraceStripMesh(geometry: BraceGeometry): MeshData {
  const builder = new MeshBuilder();
  const offset = braceExtrudeOffset(geometry);
  const { points, closed } = bracePolyline(geometry);
  const holed = Boolean(geometry.holes && geometry.holes.length > 0);
  const source = holed
    ? { points, centers: points.map(() => undefined) }
    : tessellatePolylineSegments(points, closed, geometry.arcs);
  const n = source.points.length;
  const count = closed ? n : n - 1;
  const paperThin = geometry.thickness <= 1e-9;
  const { dirs, rings, incoming, outgoing } = braceStripRings(geometry, source.points, closed);

  let u0 = 0;
  for (let i = 0; i < count; i++) {
    const startRing = rings[i];
    const endRing = rings[(i + 1) % n];
    if (!startRing || !endRing || !dirs[i]) continue;
    const a = source.points[i];
    const b = source.points[(i + 1) % n];
    let startN: Vec3 | undefined;
    let endN: Vec3 | undefined;
    const center = source.centers[i];
    if (center) {
      const t0 = arcTangent(a, center, a, b);
      const t1 = arcTangent(b, center, a, b);
      if (t0) startN = vec.normalize(vec.cross(t0, offset));
      if (t1) endN = vec.normalize(vec.cross(t1, offset));
    }
    const face = holed ? pathLogicalFace(geometry, i) : null;
    const faceHoles = face ? pathFaceHoles(geometry, face) : [];
    u0 =
      face && faceHoles.length > 0
        ? addHoledStripSegment(
            builder,
            startRing,
            endRing,
            a,
            b,
            offset,
            u0,
            geometry.thickness,
            face,
            faceHoles,
            startN,
            endN
          )
        : addStripSegment(builder, startRing, endRing, a, b, offset, u0, geometry.thickness, startN, endN);
  }

  if (!paperThin) {
    for (let i = 0; i < n; i++) {
      const ring = rings[i];
      if (!ring) continue;
      const dIn = incoming[i];
      const dOut = outgoing[i];
      if (dIn && !dOut) capStripRing(builder, ring, geometry.thickness, false);
      if (dOut && !dIn) capStripRing(builder, ring, geometry.thickness, true);
    }
  }

  return builder.build();
}

function extrudeBraceSegment(start: Vec3, end: Vec3, geometry: BraceGeometry): MeshData {
  const { thickness } = geometry;
  const axisVec = vec.sub(end, start);
  const length = vec.length(axisVec);
  const builder = new MeshBuilder();
  if (length < 1e-9) return builder.build();

  const { axis, right, up } = braceCrossSectionBasis(axisVec, geometry.rotation ?? 0);
  const hw = thickness / 2;
  const hh = hw;
  const offsets = braceSectionOffsets(right, up, hw, hh);
  const startRing = offsets.map((o) => vec.add(start, o));
  const endRing = offsets.map((o) => vec.add(end, o));
  stitchBraceSides(builder, startRing, endRing, axis, thickness, length);
  capBraceRing(builder, startRing, offsets, vec.scale(axis, -1), right, up, hw, hh, true);
  capBraceRing(builder, endRing, offsets, axis, right, up, hw, hh, false);
  return builder.build();
}

function extrudeBracePolyline(
  points: Vec3[],
  closed: boolean,
  geometry: BraceGeometry,
  centers: (Vec3 | undefined)[] = []
): MeshData {
  const builder = new MeshBuilder();
  const n = points.length;
  if (n < 2 || (closed && n < 3)) return builder.build();

  const dirs: Vec3[] = [];
  const frames: { axis: Vec3; right: Vec3; up: Vec3 }[] = [];
  const count = closed ? n : n - 1;
  for (let i = 0; i < count; i++) {
    const axisVec = vec.sub(points[(i + 1) % n], points[i]);
    const length = vec.length(axisVec);
    if (length < 1e-9) return builder.build();
    const axis = vec.scale(axisVec, 1 / length);
    dirs.push(axis);
    frames.push(
      i === 0
        ? braceCrossSectionBasis(axisVec, geometry.rotation ?? 0)
        : transportFrame(frames[i - 1], axis)
    );
  }

  const hw = geometry.thickness / 2;
  const hh = hw;
  const rings: Vec3[][] = [];
  for (let i = 0; i < n; i++) {
    const incoming = closed || i > 0;
    const outgoing = closed || i < n - 1;
    if (incoming && outgoing) {
      const inIndex = (i - 1 + count) % count;
      const outIndex = closed ? i % count : i;
      const frame = frames[inIndex];
      const offsets = braceSectionOffsets(frame.right, frame.up, hw, hh);
      rings.push(miterRing(points[i], offsets, dirs[inIndex], dirs[outIndex]));
    } else {
      const frame = frames[i === 0 ? 0 : count - 1];
      const offsets = braceSectionOffsets(frame.right, frame.up, hw, hh);
      rings.push(offsets.map((o) => vec.add(points[i], o)));
    }
  }

  let v0 = 0;
  for (let i = 0; i < count; i++) {
    const startRing = rings[i];
    const endRing = rings[(i + 1) % n];
    const length = vec.length(vec.sub(points[(i + 1) % n], points[i]));
    const center = centers[i];
    const startAxis = center
      ? (arcTangent(points[i], center, points[i], points[(i + 1) % n]) ?? dirs[i])
      : dirs[i];
    const endAxis = center
      ? (arcTangent(points[(i + 1) % n], center, points[i], points[(i + 1) % n]) ?? dirs[i])
      : dirs[i];
    stitchBraceSides(
      builder,
      startRing,
      endRing,
      dirs[i],
      geometry.thickness,
      length,
      v0,
      startAxis,
      endAxis
    );
    v0 += length;
  }

  if (!closed) {
    const startFrame = frames[0];
    const endFrame = frames[count - 1];
    const startOffsets = braceSectionOffsets(startFrame.right, startFrame.up, hw, hh);
    const endOffsets = braceSectionOffsets(endFrame.right, endFrame.up, hw, hh);
    capBraceRing(builder, rings[0], startOffsets, vec.scale(dirs[0], -1), startFrame.right, startFrame.up, hw, hh, true);
    capBraceRing(
      builder,
      rings[n - 1],
      endOffsets,
      dirs[count - 1],
      endFrame.right,
      endFrame.up,
      hw,
      hh,
      false
    );
  }

  return builder.build();
}

function stitchBraceSides(
  builder: MeshBuilder,
  startRing: Vec3[],
  endRing: Vec3[],
  axis: Vec3,
  thickness: number,
  length: number,
  v0 = 0,
  startAxis = axis,
  endAxis = axis
): void {
  const sideSpan = [thickness, thickness, thickness, thickness];
  const v1 = v0 + length;
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    const startEdge = vec.sub(startRing[j], startRing[i]);
    const endEdge = vec.sub(endRing[j], endRing[i]);
    const face = vec.normalize(vec.cross(startEdge, axis));
    if (vec.length(face) < 1e-9) continue;
    const startN = facingNormal(vec.normalize(vec.cross(startEdge, startAxis)), face);
    const endN = facingNormal(vec.normalize(vec.cross(endEdge, endAxis)), face);
    const span = sideSpan[i];
    const a = builder.vertex(startRing[i], startN, 0, v0);
    const b = builder.vertex(startRing[j], startN, span, v0);
    const c = builder.vertex(endRing[j], endN, span, v1);
    const d = builder.vertex(endRing[i], endN, 0, v1);
    builder.quad(a, b, c, d);
  }
}

function capBraceRing(
  builder: MeshBuilder,
  ring: Vec3[],
  offsets: Vec3[],
  normal: Vec3,
  right: Vec3,
  up: Vec3,
  hw: number,
  hh: number,
  reverse: boolean
): void {
  const idx = ring.map((p, i) => {
    const local = offsets[i];
    return builder.vertex(p, normal, vec.dot(local, right) + hw, vec.dot(local, up) + hh);
  });
  if (reverse) builder.quad(idx[0], idx[3], idx[2], idx[1]);
  else builder.quad(idx[0], idx[1], idx[2], idx[3]);
}

/* ------------------------------------------------------------------ */
/* plane (§4.3)                                                        */
/* ------------------------------------------------------------------ */

type Point2D = HolePoint2D;

function signedArea(points: Point2D[]): number {
  return signedArea2(points);
}

/** データ上の厚み 0 を、描画・当たり判定ではこの厚みとして扱う。側面は出さない。 */
export const ZERO_PLANE_THICKNESS = 0.005;

export function effectivePlaneThickness(thickness: number): number {
  return thickness <= 1e-9 ? ZERO_PLANE_THICKNESS : thickness;
}

/** 平面上の 2D 基底（tangent / bitangent）。cross(tangent, bitangent) === normal */
export function planeBasis(normal: Vec3): { tangent: Vec3; bitangent: Vec3 } {
  const n = vec.normalize(normal);
  const ref = Math.abs(n.y) > 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
  const tangent = vec.normalize(vec.cross(n, ref));
  const bitangent = vec.normalize(vec.cross(n, tangent));
  return { tangent, bitangent };
}

/**
 * 1–2点目を1辺、3点目からその辺への垂線距離を幅とする長方形。
 * 3点目自体は角でなくてよい。退化（辺がゼロ・一直線・幅ゼロ）は null。
 * 頂点は法線から見て反時計回り。
 */
export function rectangleFromThreePoints(
  a: Vec3,
  b: Vec3,
  c: Vec3
): { points: Vec3[]; normal: Vec3 } | null {
  const edge = vec.sub(b, a);
  const edgeLen = vec.length(edge);
  if (edgeLen < 1e-9) return null;
  const edgeDir = vec.scale(edge, 1 / edgeLen);

  const toC = vec.sub(c, a);
  const along = vec.dot(toC, edgeDir);
  const perp = vec.sub(toC, vec.scale(edgeDir, along));
  const width = vec.length(perp);
  if (width < 1e-9) return null;
  const widthDir = vec.scale(perp, 1 / width);

  const normal = vec.normalize(vec.cross(edgeDir, widthDir));
  if (vec.length(normal) < 1e-9) return null;

  const offset = vec.scale(widthDir, width);
  return {
    points: [{ ...a }, { ...b }, vec.add(b, offset), vec.add(a, offset)],
    normal,
  };
}

/**
 * プレーン（閉じた多角形 + 厚み）のメッシュを生成する。
 * 点群の平面を厚み方向の中面とし、`normal` の両側へ ±thickness/2 押し出す。
 * thickness 0 は ZERO_PLANE_THICKNESS で押し出すが、側面は出さない。
 * `normal` 省略時は点群から自動計算する（§4.3）。
 */
function projectToPlane2D(points: Vec3[], origin: Vec3, tangent: Vec3, bitangent: Vec3): Point2D[] {
  return points.map((p) => {
    const d = vec.sub(p, origin);
    return { x: vec.dot(d, tangent), y: vec.dot(d, bitangent) };
  });
}

function unprojectFromPlane2D(point: Point2D, origin: Vec3, tangent: Vec3, bitangent: Vec3): Vec3 {
  return vec.add(origin, vec.add(vec.scale(tangent, point.x), vec.scale(bitangent, point.y)));
}

function planeHoleWorldRings(geometry: PlaneGeometry, start: Vec3[], offsetDir: Vec3): Vec3[][] {
  const holes = geometry.holes;
  if (!holes || holes.length === 0) return [];
  const origin = start[0];
  return holes.map((hole) =>
    hole.map((p) => vec.sub(p, vec.scale(offsetDir, vec.dot(vec.sub(p, origin), offsetDir))))
  );
}

export function planeHasHoles(geometry: PlaneGeometry): boolean {
  return Boolean(
    (geometry.holes && geometry.holes.length > 0) ||
      (geometry.sideHoles && geometry.sideHoles.length > 0)
  );
}

function fillHoleCount(geometry: PlaneGeometry): number {
  return (geometry.holes?.length ?? 0) + (geometry.sideHoles?.length ?? 0);
}

/** 始面の三角化。メッシュ・衝突・SDF が同じ結果を使う。 */
export interface TessellatedSlab {
  slab: PlaneSlab;
  origin: Vec3;
  tangent: Vec3;
  bitangent: Vec3;
  vertices2: Point2D[];
  indices: number[];
  holeRings: Vec3[][];
  /** 輪郭の各辺。円弧なら中心。側面法線用。 */
  centers: (Vec3 | undefined)[];
}

export function tessellateSlab(geometry: PlaneGeometry): TessellatedSlab | null {
  const slab = planeSlab(geometry);
  if (!slab) return null;
  const { start, offset } = slab;
  const offsetLen = vec.length(offset);
  if (offsetLen < 1e-12) return null;
  const offsetDir = vec.scale(offset, 1 / offsetLen);
  const { tangent, bitangent } = planeBasis(offsetDir);
  const origin = start[0];
  let ordered = start;
  let projected = projectToPlane2D(start, origin, tangent, bitangent);
  const area = signedArea(projected);
  if (Math.abs(area) < 1e-12) return null;
  if (area < 0) {
    ordered = [...start].reverse();
    projected = [...projected].reverse();
  }
  const holeRings = planeHoleWorldRings(geometry, ordered, offsetDir);
  const holeProjected = holeRings.map((ring) => projectToPlane2D(ring, origin, tangent, bitangent));
  const tessellated =
    holeProjected.length > 0
      ? triangulatePolygonWithHoles(projected, holeProjected)
      : { vertices: projected, indices: triangulatePolygon(projected) };
  const outline = tessellatePolylineSegments(geometry.points, true, geometry.arcs);
  const centers =
    ordered === start ? outline.centers : reverseClosedSegmentCenters(outline.centers);
  return {
    slab: { ...slab, start: ordered },
    origin,
    tangent,
    bitangent,
    vertices2: tessellated.vertices,
    indices: tessellated.indices,
    holeRings,
    centers,
  };
}

export interface PathFace {
  segment: number;
  origin: Vec3;
  uAxis: Vec3;
  vAxis: Vec3;
  normal: Vec3;
  points: Vec3[];
}

/** 論理辺の平行四辺形（始点・終点・押し出し）。UV の基準。 */
export function pathLogicalFace(geometry: BraceGeometry, segment: number): PathFace | null {
  const { points, closed } = bracePolyline(geometry);
  const n = points.length;
  const count = closed ? n : n - 1;
  if (segment < 0 || segment >= count) return null;
  const offset = braceExtrudeOffset(geometry);
  if (vec.length(offset) < 1e-9) return null;
  const origin = points[segment];
  const uAxis = vec.sub(points[(segment + 1) % n], origin);
  if (vec.length(uAxis) < 1e-9) return null;
  const normal = vec.normalize(vec.cross(uAxis, offset));
  if (vec.length(normal) < 1e-9) return null;
  return {
    segment,
    origin,
    uAxis,
    vAxis: offset,
    normal,
    points: [origin, vec.add(origin, uAxis), vec.add(vec.add(origin, uAxis), offset), vec.add(origin, offset)],
  };
}

export function pathFacePlane(face: PathFace): PlaneGeometry {
  return { points: face.points, thickness: 0, normal: face.normal };
}

export function worldToPathUv(face: PathFace, point: Vec3): { u: number; v: number } {
  const d = vec.sub(point, face.origin);
  const uu = vec.dot(face.uAxis, face.uAxis);
  const vv = vec.dot(face.vAxis, face.vAxis);
  const uv = vec.dot(face.uAxis, face.vAxis);
  const du = vec.dot(d, face.uAxis);
  const dv = vec.dot(d, face.vAxis);
  const den = uu * vv - uv * uv;
  if (Math.abs(den) < 1e-12) return { u: 0, v: 0 };
  return { u: (du * vv - dv * uv) / den, v: (uu * dv - uv * du) / den };
}

export function pathUvToWorld(face: PathFace, uv: { u: number; v: number }): Vec3 {
  return vec.add(face.origin, vec.add(vec.scale(face.uAxis, uv.u), vec.scale(face.vAxis, uv.v)));
}

export function pathHoleWorldPolygon(geometry: BraceGeometry, hole: BracePathHole): Vec3[] {
  const face = pathLogicalFace(geometry, hole.segment);
  if (!face) return [];
  return hole.uv.map((uv) => pathUvToWorld(face, uv));
}

/** 押し出し／厚みの側面。輪郭の1辺 × スラブの offset。 */
export function fillSideFace(geometry: BraceGeometry | PlaneGeometry, edge: number): PathFace | null {
  const plane = asPlaneGeometry(geometry);
  const slab = planeSlab(plane);
  if (!slab || vec.length(slab.offset) < 1e-9) return null;
  const points = plane.points;
  const n = points.length;
  if (edge < 0 || edge >= n) return null;
  const origin = points[edge];
  const uAxis = vec.sub(points[(edge + 1) % n], origin);
  if (vec.length(uAxis) < 1e-9) return null;
  const normal = vec.normalize(vec.cross(uAxis, slab.offset));
  if (vec.length(normal) < 1e-9) return null;
  return {
    segment: edge,
    origin,
    uAxis,
    vAxis: slab.offset,
    normal,
    points: [
      origin,
      vec.add(origin, uAxis),
      vec.add(vec.add(origin, uAxis), slab.offset),
      vec.add(origin, slab.offset),
    ],
  };
}

export function nearestFillFace(
  geometry: BraceGeometry | PlaneGeometry,
  point: Vec3
): { kind: 'cap' } | { kind: 'side'; face: PathFace } {
  const plane = asPlaneGeometry(geometry);
  const slab = planeSlab(plane);
  if (!slab) return { kind: 'cap' };

  const distanceToCap = (points: Vec3[], normal: Vec3): number => {
    const origin = points[0];
    const d = vec.dot(vec.sub(point, origin), normal);
    const projected = vec.sub(point, vec.scale(normal, d));
    const { tangent, bitangent } = planeBasis(normal);
    const outer = projectToPlane2D(points, origin, tangent, bitangent);
    const uv = projectToPlane2D([projected], origin, tangent, bitangent)[0];
    if (pointInPolygon(uv, outer, true)) return Math.abs(d);
    let best = Infinity;
    for (let i = 0; i < points.length; i++) {
      const a = points[i];
      const b = points[(i + 1) % points.length];
      const ab = vec.sub(b, a);
      const den = vec.dot(ab, ab);
      const t = den < 1e-12 ? 0 : Math.min(1, Math.max(0, vec.dot(vec.sub(projected, a), ab) / den));
      const closest = vec.add(a, vec.scale(ab, t));
      best = Math.min(best, vec.length(vec.sub(point, closest)));
    }
    return best;
  };

  let best: { kind: 'cap' } | { kind: 'side'; face: PathFace } = { kind: 'cap' };
  let bestDist = distanceToCap(slab.start, slab.normal);
  const end = slab.start.map((p) => vec.add(p, slab.offset));
  const endDist = distanceToCap(end, slab.normal);
  if (endDist < bestDist) bestDist = endDist;

  if (vec.length(slab.offset) >= 1e-9) {
    for (let i = 0; i < plane.points.length; i++) {
      const face = fillSideFace(plane, i);
      if (!face) continue;
      const uv = worldToPathUv(face, point);
      const clamped = { u: Math.min(1, Math.max(0, uv.u)), v: Math.min(1, Math.max(0, uv.v)) };
      const onFace = pathUvToWorld(face, clamped);
      const dist = vec.length(vec.sub(point, onFace));
      if (dist < bestDist) {
        bestDist = dist;
        best = { kind: 'side', face };
      }
    }
  }
  return best;
}

function pointOnPathFace(point: Vec3, face: PathFace): boolean {
  if (Math.abs(vec.dot(vec.sub(point, face.origin), face.normal)) > 1e-5) return false;
  const uv = worldToPathUv(face, point);
  return uv.u >= -1e-6 && uv.u <= 1 + 1e-6 && uv.v >= -1e-6 && uv.v <= 1 + 1e-6;
}

function polygonCentroid3(points: Vec3[]): Vec3 {
  let x = 0;
  let y = 0;
  let z = 0;
  for (const point of points) {
    x += point.x;
    y += point.y;
    z += point.z;
  }
  const n = points.length;
  return { x: x / n, y: y / n, z: z / n };
}

export function fillSideFaceHoles(geometry: BraceGeometry | PlaneGeometry, face: PathFace): Vec3[][] {
  const plane = asPlaneGeometry(geometry);
  const holes = plane.sideHoles;
  if (!holes || holes.length === 0) return [];
  const slab = planeSlab(plane);
  const result: Vec3[][] = [];
  for (const hole of holes) {
    const source = fillSideFace(plane, hole.segment);
    const world = source ? hole.uv.map((uv) => pathUvToWorld(source, uv)) : [];
    if (!source || world.length < 3) continue;
    if (hole.segment === face.segment) {
      const clipped = clipWorldPolygonToFace(world, face);
      if (clipped.length >= 3) result.push(clipped);
      continue;
    }
    const projected = projectPolygonAlong(world, source.normal, face.origin, face.normal);
    if (!projected) continue;
    const clipped = clipWorldPolygonToFace(projected, face);
    if (clipped.length < 3) continue;
    if (slab) {
      const onFace = polygonCentroid3(clipped);
      const from = intersectRayPlane(onFace, vec.scale(source.normal, -1), source.origin, source.normal);
      if (!from) continue;
      const exit = exitThroughFillSolid(plane, slab, from, source.normal, hole.segment);
      if (!exit || !pointOnPathFace(exit, face)) continue;
    }
    result.push(clipped);
  }
  return result;
}

function pointOnCapFace(point: Vec3, points: Vec3[], normal: Vec3): boolean {
  const origin = points[0];
  const { tangent, bitangent } = planeBasis(normal);
  const outer = projectToPlane2D(points, origin, tangent, bitangent);
  const uv = projectToPlane2D([point], origin, tangent, bitangent)[0];
  return pointInPolygon(uv, outer, true);
}

/** 側面穴の頂点から立体を貫いて、最初に当たる反対側の点。 */
function exitThroughFillSolid(
  geometry: PlaneGeometry,
  slab: PlaneSlab,
  origin: Vec3,
  direction: Vec3,
  skipSegment: number
): Vec3 | null {
  let best: Vec3 | null = null;
  let bestT = Infinity;
  const consider = (hit: Vec3) => {
    const t = vec.dot(vec.sub(hit, origin), direction);
    if (t < 1e-6 || t >= bestT) return;
    bestT = t;
    best = hit;
  };

  const startHit = intersectRayPlane(origin, direction, slab.start[0], slab.normal);
  if (startHit && pointOnCapFace(startHit, slab.start, slab.normal)) consider(startHit);
  const endPoints = slab.start.map((p) => vec.add(p, slab.offset));
  const endHit = intersectRayPlane(origin, direction, endPoints[0], slab.normal);
  if (endHit && pointOnCapFace(endHit, endPoints, slab.normal)) consider(endHit);

  for (let i = 0; i < geometry.points.length; i++) {
    if (i === skipSegment) continue;
    const face = fillSideFace(geometry, i);
    if (!face) continue;
    const hit = intersectRayPlane(origin, direction, face.origin, face.normal);
    if (!hit) continue;
    const uv = worldToPathUv(face, hit);
    if (uv.u < -1e-6 || uv.u > 1 + 1e-6 || uv.v < -1e-6 || uv.v > 1 + 1e-6) continue;
    consider(hit);
  }
  return best;
}

/** 側面穴を貫く内壁。始面穴と同じく、輪郭の1辺 × 貫通方向。 */
function addSideHoleWalls(builder: MeshBuilder, geometry: PlaneGeometry, slab: PlaneSlab): void {
  const holes = geometry.sideHoles;
  if (!holes || holes.length === 0) return;
  for (const hole of holes) {
    const source = fillSideFace(geometry, hole.segment);
    if (!source) continue;
    const world = hole.uv.map((uv) => pathUvToWorld(source, uv));
    if (world.length < 3) continue;
    const inward = source.normal;
    if (vec.length(inward) < 1e-9) continue;
    const exits: Vec3[] = [];
    let ok = true;
    for (const point of world) {
      const exit = exitThroughFillSolid(geometry, slab, point, inward, hole.segment);
      if (!exit) {
        ok = false;
        break;
      }
      exits.push(exit);
    }
    if (!ok) continue;

    const { tangent, bitangent } = planeBasis(inward);
    const projected = projectToPlane2D(world, world[0], tangent, bitangent);
    const reverse = signedArea(projected) >= 0;
    const entry = reverse ? [...world].reverse() : world;
    const exit = reverse ? [...exits].reverse() : exits;
    for (let i = 0; i < entry.length; i++) {
      const j = (i + 1) % entry.length;
      const edge = vec.sub(entry[j], entry[i]);
      const edgeLength = vec.length(edge);
      const along = vec.sub(exit[i], entry[i]);
      const alongLen = vec.length(along);
      if (edgeLength < 1e-9 || alongLen < 1e-9) continue;
      const sideNormal = vec.normalize(vec.cross(inward, edge));
      const a = builder.vertex(entry[i], sideNormal, 0, 0);
      const b = builder.vertex(entry[j], sideNormal, edgeLength, 0);
      const c = builder.vertex(exit[j], sideNormal, edgeLength, alongLen);
      const d = builder.vertex(exit[i], sideNormal, 0, alongLen);
      builder.quad(a, b, c, d);
    }
  }
}

export function nearestPathFace(geometry: BraceGeometry, point: Vec3): PathFace | null {
  const { points, closed } = bracePolyline(geometry);
  const n = points.length;
  const count = closed ? n : n - 1;
  let best: PathFace | null = null;
  let bestDist = Infinity;
  for (let i = 0; i < count; i++) {
    const face = pathLogicalFace(geometry, i);
    if (!face) continue;
    const uv = worldToPathUv(face, point);
    const clamped = { u: Math.min(1, Math.max(0, uv.u)), v: Math.min(1, Math.max(0, uv.v)) };
    const onFace = pathUvToWorld(face, clamped);
    const dist = vec.length(vec.sub(point, onFace));
    if (dist < bestDist) {
      bestDist = dist;
      best = face;
    }
  }
  return best;
}

export function intersectRayPlane(
  origin: Vec3,
  direction: Vec3,
  planePoint: Vec3,
  planeNormal: Vec3
): Vec3 | null {
  const den = vec.dot(direction, planeNormal);
  if (Math.abs(den) < 1e-9) return null;
  const t = vec.dot(vec.sub(planePoint, origin), planeNormal) / den;
  if (t < 0) return null;
  return vec.add(origin, vec.scale(direction, t));
}

function projectPolygonAlong(
  polygon: Vec3[],
  direction: Vec3,
  planePoint: Vec3,
  planeNormal: Vec3
): Vec3[] | null {
  const den = vec.dot(direction, planeNormal);
  if (Math.abs(den) < 1e-7) return null;
  return polygon.map((p) => {
    const t = vec.dot(vec.sub(planePoint, p), planeNormal) / den;
    return vec.add(p, vec.scale(direction, t));
  });
}

function clipWorldPolygonToFace(polygon: Vec3[], face: PathFace): Vec3[] {
  const origin = face.origin;
  const { tangent, bitangent } = planeBasis(face.normal);
  const subject = projectToPlane2D(polygon, origin, tangent, bitangent);
  const clip = projectToPlane2D(face.points, origin, tangent, bitangent);
  const clipped = clipPolygon(subject, clip);
  return clipped.map((p) => unprojectFromPlane2D(p, origin, tangent, bitangent));
}

/** 描いた穴が、その帯面（1辺）に残す 2D 穴。他の辺へは投影しない。 */
export function pathFaceHoles(geometry: BraceGeometry, face: PathFace): Vec3[][] {
  const holes = geometry.holes;
  if (!holes || holes.length === 0) return [];
  const result: Vec3[][] = [];
  for (const hole of holes) {
    if (hole.segment !== face.segment) continue;
    const world = pathHoleWorldPolygon(geometry, hole);
    if (world.length < 3) continue;
    const clipped = clipWorldPolygonToFace(world, face);
    if (clipped.length >= 3) result.push(clipped);
  }
  return result;
}

export function addFillHole(geometry: BraceGeometry | PlaneGeometry, points: Vec3[]): BraceGeometry | null {
  if (points.length < 3 || points.length > MAX_PLANE_POINTS) return null;
  const plane = asPlaneGeometry(geometry);
  if (fillHoleCount(plane) >= MAX_HOLES) return null;
  const slab = planeSlab(plane);
  if (!slab) return null;
  const origin = slab.start[0];
  const { tangent, bitangent } = planeBasis(slab.normal);
  const outer = projectToPlane2D(slab.start, origin, tangent, bitangent);
  const projected = projectToPlane2D(points, origin, tangent, bitangent);
  if (Math.abs(signedArea(projected)) < 1e-12) return null;
  if (polygonSelfIntersects(projected)) return null;
  if (!polygonInsidePolygon(projected, outer)) return null;
  const onPlane = points.map((p) =>
    vec.sub(p, vec.scale(slab.normal, vec.dot(vec.sub(p, plane.points[0]), slab.normal)))
  );
  const holes = [...(plane.holes ?? []), onPlane];
  return asFillGeometry(clonePlaneGeometry(plane, { holes }));
}

export function addFillSideHole(
  geometry: BraceGeometry | PlaneGeometry,
  edge: number,
  uv: { u: number; v: number }[]
): BraceGeometry | null {
  if (uv.length < 3 || uv.length > MAX_PLANE_POINTS) return null;
  const plane = asPlaneGeometry(geometry);
  if (fillHoleCount(plane) >= MAX_HOLES) return null;
  const face = fillSideFace(plane, edge);
  if (!face) return null;
  const outer: HolePoint2D[] = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
  ];
  const projected = uv.map((p) => ({ x: p.u, y: p.v }));
  if (Math.abs(signedArea(projected)) < 1e-12) return null;
  if (polygonSelfIntersects(projected)) return null;
  if (!polygonInsidePolygon(projected, outer)) return null;
  const sideHoles = [
    ...(plane.sideHoles ?? []),
    { segment: edge, uv: uv.map((p) => ({ u: p.u, v: p.v })) },
  ];
  return asFillGeometry(clonePlaneGeometry(plane, { sideHoles }));
}

export function addPathHole(
  geometry: BraceGeometry,
  segment: number,
  uv: { u: number; v: number }[]
): BraceGeometry | null {
  if (uv.length < 3 || uv.length > MAX_PLANE_POINTS) return null;
  if ((geometry.holes?.length ?? 0) >= MAX_HOLES) return null;
  const face = pathLogicalFace(geometry, segment);
  if (!face) return null;
  const outer: HolePoint2D[] = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
  ];
  const projected = uv.map((p) => ({ x: p.u, y: p.v }));
  if (Math.abs(signedArea(projected)) < 1e-12) return null;
  if (polygonSelfIntersects(projected)) return null;
  if (!polygonInsidePolygon(projected, outer)) return null;
  const next = cloneBraceGeometry(geometry);
  next.holes = [...(geometry.holes ?? []), { segment, uv: uv.map((p) => ({ u: p.u, v: p.v })) }];
  return next;
}

export function buildPlaneMesh(geometry: PlaneGeometry): MeshData {
  const builder = new MeshBuilder();
  const tessellated = tessellateSlab(geometry);
  if (!tessellated) return builder.build();

  const { slab, origin, tangent, bitangent, vertices2, indices, holeRings } = tessellated;
  const { start: ordered, offset, paperThin } = slab;
  const offsetLen = vec.length(offset);
  const offsetDir = vec.scale(offset, 1 / offsetLen);

  const startNormal = vec.scale(offsetDir, -1);
  const endVerts = vertices2.map((p) =>
    vec.add(unprojectFromPlane2D(p, origin, tangent, bitangent), offset)
  );
  const startVerts = vertices2.map((p) => unprojectFromPlane2D(p, origin, tangent, bitangent));

  const endIdx = endVerts.map((p, i) => builder.vertex(p, offsetDir, vertices2[i].x, vertices2[i].y));
  for (let i = 0; i < indices.length; i += 3) {
    builder.triangle(endIdx[indices[i]], endIdx[indices[i + 1]], endIdx[indices[i + 2]]);
  }

  const startIdx = startVerts.map((p, i) =>
    builder.vertex(p, startNormal, vertices2[i].x, vertices2[i].y)
  );
  for (let i = 0; i < indices.length; i += 3) {
    builder.triangle(startIdx[indices[i + 2]], startIdx[indices[i + 1]], startIdx[indices[i]]);
  }

  const sideMeshes: MeshData[] = [];
  if (!paperThin) {
    if (geometry.sideHoles && geometry.sideHoles.length > 0) {
      for (let i = 0; i < geometry.points.length; i++) {
        const face = fillSideFace(geometry, i);
        if (!face) continue;
        const holes = fillSideFaceHoles(geometry, face);
        sideMeshes.push(
          buildPlaneMesh({
            points: face.points,
            thickness: 0,
            normal: face.normal,
            ...(holes.length > 0 ? { holes } : {}),
          })
        );
      }
      addSideHoleWalls(builder, geometry, slab);
    } else {
      let perimeter = 0;
      const centers = tessellated.centers;
      for (let i = 0; i < ordered.length; i++) {
        const j = (i + 1) % ordered.length;
        const edge = vec.sub(ordered[j], ordered[i]);
        const edgeLength = vec.length(edge);
        if (edgeLength < 1e-9) continue;
        const face = vec.normalize(vec.cross(edge, offsetDir));
        if (vec.length(face) < 1e-9) continue;
        const center = centers[i];
        let n0 = face;
        let n1 = face;
        if (center) {
          const t0 = arcTangent(ordered[i], center, ordered[i], ordered[j]);
          const t1 = arcTangent(ordered[j], center, ordered[i], ordered[j]);
          if (t0) n0 = facingNormal(vec.normalize(vec.cross(t0, offsetDir)), face);
          if (t1) n1 = facingNormal(vec.normalize(vec.cross(t1, offsetDir)), face);
        }
        const u0 = perimeter;
        const u1 = perimeter + edgeLength;
        const a = builder.vertex(ordered[i], n0, u0, 0);
        const b = builder.vertex(ordered[j], n1, u1, 0);
        const c = builder.vertex(vec.add(ordered[j], offset), n1, u1, offsetLen);
        const d = builder.vertex(vec.add(ordered[i], offset), n0, u0, offsetLen);
        builder.quad(a, b, c, d);
        perimeter = u1;
      }
    }
    const holeProjected = holeRings.map((ring) => projectToPlane2D(ring, origin, tangent, bitangent));
    const merged = mergeOverlappingHoles(holeProjected);
    for (const ring of merged) {
      const orderedHole = ensureWinding(ring, signedArea(vertices2) < 0);
      const hole3 = orderedHole.map((p) => unprojectFromPlane2D(p, origin, tangent, bitangent));
      for (let i = 0; i < hole3.length; i++) {
        const j = (i + 1) % hole3.length;
        const edge = vec.sub(hole3[j], hole3[i]);
        const edgeLength = vec.length(edge);
        if (edgeLength < 1e-9) continue;
        const sideNormal = vec.normalize(vec.cross(offsetDir, edge));
        const a = builder.vertex(hole3[i], sideNormal, 0, 0);
        const b = builder.vertex(hole3[j], sideNormal, edgeLength, 0);
        const c = builder.vertex(vec.add(hole3[j], offset), sideNormal, edgeLength, offsetLen);
        const d = builder.vertex(vec.add(hole3[i], offset), sideNormal, 0, offsetLen);
        builder.quad(a, b, c, d);
      }
    }
  }

  const mesh = builder.build();
  return sideMeshes.length > 0 ? mergeMeshes([mesh, ...sideMeshes]) : mesh;
}

/* ------------------------------------------------------------------ */
/* 共通ユーティリティ                                                  */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* stair                                                               */
/* ------------------------------------------------------------------ */

/** 踏面の目安。段数は水平距離をこの値で割って丸める。 */
export const STAIR_TREAD = 0.2;
/** 階段の幅（進行方向に対して左右均等）。 */
export const STAIR_WIDTH = 1;
export const MAX_STAIR_STEPS = 128;
export const STAIR_MIN_HORIZONTAL = 1e-4;

export interface StairLayout {
  start: Vec3;
  end: Vec3;
  steps: number;
  tread: number;
  rise: number;
  along: Vec3;
  side: Vec3;
  width: number;
}

/** 中心線2点から段数・踏面・蹴上を決める。水平距離がほぼ0、幅が非正なら null。 */
export function stairLayout(start: Vec3, end: Vec3, width = STAIR_WIDTH): StairLayout | null {
  if (width <= 0) return null;
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const horiz = Math.hypot(dx, dz);
  if (horiz < STAIR_MIN_HORIZONTAL) return null;
  const steps = Math.min(MAX_STAIR_STEPS, Math.max(1, Math.round(horiz / STAIR_TREAD)));
  const along = { x: dx / horiz, y: 0, z: dz / horiz };
  return {
    start,
    end,
    steps,
    tread: horiz / steps,
    rise: (end.y - start.y) / steps,
    along,
    side: { x: -along.z, y: 0, z: along.x },
    width,
  };
}

/**
 * 1–2点目を幅、3点目からその辺への水平垂線距離を長さとする階段。
 * 3点目自体は角でなくてよい。退化（幅ゼロ・長さゼロ）は null。
 * start/end は幅の中心線。end.y は3点目の高さ。
 */
export function stairFromThreePoints(a: Vec3, b: Vec3, c: Vec3): StairGeometry | null {
  const widthDx = b.x - a.x;
  const widthDz = b.z - a.z;
  const width = Math.hypot(widthDx, widthDz);
  if (width < STAIR_MIN_HORIZONTAL) return null;
  const start: Vec3 = {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
    z: (a.z + b.z) / 2,
  };
  const sideX = widthDx / width;
  const sideZ = widthDz / width;
  const signed = (c.x - start.x) * -sideZ + (c.z - start.z) * sideX;
  if (Math.abs(signed) < STAIR_MIN_HORIZONTAL) return null;
  const alongX = signed >= 0 ? -sideZ : sideZ;
  const alongZ = signed >= 0 ? sideX : -sideX;
  const run = Math.abs(signed);
  return {
    start,
    end: { x: start.x + alongX * run, y: c.y, z: start.z + alongZ * run },
    width,
  };
}

/** 各段を厚み付き水平プレーン（箱）にする。見た目用。 */
export function stairPlanes(geometry: StairGeometry): PlaneGeometry[] {
  const layout = stairLayout(geometry.start, geometry.end, geometry.width ?? STAIR_WIDTH);
  if (!layout) return [];
  const { start, steps, tread, rise, along, side, width } = layout;
  const halfW = width / 2;
  const planes: PlaneGeometry[] = [];
  for (let i = 0; i < steps; i++) {
    const along0 = i * tread;
    const along1 = (i + 1) * tread;
    const y = start.y + (i + 0.5) * rise;
    const at = (alongDist: number, sideSign: number): Vec3 => ({
      x: start.x + along.x * alongDist + side.x * sideSign * halfW,
      y,
      z: start.z + along.z * alongDist + side.z * sideSign * halfW,
    });
    planes.push({
      points: [at(along0, -1), at(along0, 1), at(along1, 1), at(along1, -1)],
      thickness: Math.abs(rise),
      normal: { x: 0, y: 1, z: 0 },
    });
  }
  return planes;
}

/**
 * 当たり判定用。段ではなく始点〜終点の斜めプレーン1枚。
 * 厚み0のプレーンと同じく、面上を滑って歩ける。
 */
export function stairCollisionPlane(geometry: StairGeometry): PlaneGeometry | null {
  const layout = stairLayout(geometry.start, geometry.end, geometry.width ?? STAIR_WIDTH);
  if (!layout) return null;
  const { start, end, side, width } = layout;
  const halfW = width / 2;
  const at = (point: Vec3, sideSign: number): Vec3 => ({
    x: point.x + side.x * sideSign * halfW,
    y: point.y,
    z: point.z + side.z * sideSign * halfW,
  });
  const points = [at(start, -1), at(start, 1), at(end, 1), at(end, -1)];
  const along3 = vec.sub(end, start);
  const across = vec.sub(points[1], points[0]);
  let normal = vec.normalize(vec.cross(across, along3));
  if (vec.length(normal) < 1e-9) return null;
  if (normal.y < 0) normal = vec.scale(normal, -1);
  return { points, thickness: 0, normal };
}

export function buildStairMesh(geometry: StairGeometry): MeshData {
  return mergeMeshes(stairPlanes(geometry).map(buildPlaneMesh));
}

function transformObjectMesh(
  data: MeshData,
  localCenter: Vec3,
  origin: Vec3,
  yaw: number,
  mount: ObjectMountAxis
): MeshData {
  const positions = new Float32Array(data.positions.length);
  const normals = new Float32Array(data.normals.length);
  for (let i = 0; i < data.positions.length; i += 3) {
    const local = {
      x: data.positions[i] + localCenter.x,
      y: data.positions[i + 1] + localCenter.y,
      z: data.positions[i + 2] + localCenter.z,
    };
    const world = objectWorldPoint(local, origin, yaw, mount);
    positions[i] = world.x;
    positions[i + 1] = world.y;
    positions[i + 2] = world.z;
    const n = objectWorldPoint(
      { x: data.normals[i], y: data.normals[i + 1], z: data.normals[i + 2] },
      { x: 0, y: 0, z: 0 },
      yaw,
      mount
    );
    const len = Math.hypot(n.x, n.y, n.z) || 1;
    normals[i] = n.x / len;
    normals[i + 1] = n.y / len;
    normals[i + 2] = n.z / len;
  }
  return { positions, normals, uvs: data.uvs, indices: data.indices };
}

function objectPartMesh(
  bounds: ObjectBounds,
  origin: Vec3,
  yaw: number,
  mount: ObjectMountAxis
): MeshData {
  const size = {
    x: bounds.max.x - bounds.min.x,
    y: bounds.max.y - bounds.min.y,
    z: bounds.max.z - bounds.min.z,
  };
  return transformObjectMesh(
    buildBlockMesh({ origin: { x: 0, y: 0, z: 0 }, size }),
    objectBoundsCenter(bounds),
    origin,
    yaw,
    mount
  );
}

/** カタログ parts（なければ bounds の1箱）を姿勢で回す。 */
export function buildObjectMesh(entity: ObjectEntity): MeshData {
  const definition = resolveObjectDefinition(entity.definitionId);
  const yaw = entity.geometry.yaw;
  const mount = entity.geometry.mount ?? '+y';
  const origin = entity.geometry.origin;
  const parts = definition.parts ?? [{ bounds: definition.bounds }];
  const defaultMaterial = definition.material ?? entity.material;
  return mergeMeshesWithMaterials(
    parts.map((item) => ({
      mesh: objectPartMesh(item.bounds, origin, yaw, mount),
      material: item.material ?? defaultMaterial,
    }))
  );
}

function mergeMeshesWithMaterials(
  parts: readonly { mesh: MeshData; material: string }[]
): MeshData {
  const merged = mergeMeshes(parts.map((part) => part.mesh));
  const materials: string[] = [];
  const groups: MeshGroup[] = [];
  let indexOffset = 0;
  for (const part of parts) {
    let materialIndex = materials.indexOf(part.material);
    if (materialIndex < 0) {
      materialIndex = materials.length;
      materials.push(part.material);
    }
    groups.push({
      start: indexOffset,
      count: part.mesh.indices.length,
      materialIndex,
    });
    indexOffset += part.mesh.indices.length;
  }
  if (materials.length <= 1) return { ...merged, materials };
  return { ...merged, groups, materials };
}

function mergeMeshes(meshes: readonly MeshData[]): MeshData {
  let vertexCount = 0;
  let indexCount = 0;
  for (const mesh of meshes) {
    vertexCount += mesh.positions.length / 3;
    indexCount += mesh.indices.length;
  }
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);
  const indices = new Uint32Array(indexCount);
  let vertexOffset = 0;
  let indexOffset = 0;
  for (const mesh of meshes) {
    positions.set(mesh.positions, vertexOffset * 3);
    normals.set(mesh.normals, vertexOffset * 3);
    uvs.set(mesh.uvs, vertexOffset * 2);
    for (let i = 0; i < mesh.indices.length; i++) {
      indices[indexOffset + i] = mesh.indices[i] + vertexOffset;
    }
    vertexOffset += mesh.positions.length / 3;
    indexOffset += mesh.indices.length;
  }
  return { positions, normals, uvs, indices };
}

/** entity の種別に応じてメッシュを生成する */
export function buildEntityMesh(entity: Entity): MeshData {
  switch (entity.type) {
    case 'brace':
      if (entity.geometry.revolve) return buildRevolveMesh(entity.geometry);
      if (entity.geometry.cloth) return clothMeshData(entity.geometry.cloth.mesh);
      return isFillGeometry(entity.geometry)
        ? buildPlaneMesh(braceAsSlab(entity.geometry))
        : buildBraceMesh(entity.geometry);
    case 'stair':
      return buildStairMesh(entity.geometry);
    case 'object':
      return buildObjectMesh(entity);
  }
}

/** entity の代表点（選択表示・移動の基準に使う重心） */
export function entityCentroid(entity: Entity): Vec3 {
  switch (entity.type) {
    case 'brace': {
      const points = bracePolyline(entity.geometry).points;
      const sum = points.reduce((acc, p) => vec.add(acc, p), { x: 0, y: 0, z: 0 });
      return vec.scale(sum, 1 / points.length);
    }
    case 'stair':
      return vec.scale(vec.add(entity.geometry.start, entity.geometry.end), 0.5);
    case 'object': {
      const bounds = resolveObjectDefinition(entity.definitionId).bounds;
      return objectWorldPoint(
        objectBoundsCenter(bounds),
        entity.geometry.origin,
        entity.geometry.yaw,
        entity.geometry.mount ?? '+y'
      );
    }
  }
}

/** entity 全体を delta だけ平行移動した新しい geometry を返す（移動操作用） */
export function translateEntity<T extends Entity>(entity: T, delta: Vec3): T {
  switch (entity.type) {
    case 'brace': {
      const arcs = entity.geometry.arcs?.map((arc) => ({
        ...arc,
        center: vec.add(arc.center, delta),
      }));
      if (isFillGeometry(entity.geometry)) {
        const capHoles = entity.geometry.capHoles?.map((hole) => hole.map((p) => vec.add(p, delta)));
        const points = (entity.geometry.points ?? bracePolyline(entity.geometry).points).map((p) =>
          vec.add(p, delta)
        );
        const cloth = entity.geometry.cloth
          ? translateClothBake(entity.geometry.cloth, delta)
          : undefined;
        return {
          ...entity,
          geometry: {
            ...entity.geometry,
            start: points[0],
            end: points[points.length - 1],
            points,
            ...(arcs ? { arcs } : {}),
            ...(capHoles ? { capHoles } : {}),
            ...(cloth ? { cloth } : {}),
          },
        };
      }
      const start = vec.add(entity.geometry.start, delta);
      const end = vec.add(entity.geometry.end, delta);
      const points = entity.geometry.points?.map((p) => vec.add(p, delta));
      const cloth = entity.geometry.cloth
        ? translateClothBake(entity.geometry.cloth, delta)
        : undefined;
      return {
        ...entity,
        geometry: {
          ...entity.geometry,
          start,
          end,
          ...(points ? { points } : {}),
          ...(arcs ? { arcs } : {}),
          ...(cloth ? { cloth } : {}),
        },
      };
    }
    case 'stair':
      return {
        ...entity,
        geometry: {
          start: vec.add(entity.geometry.start, delta),
          end: vec.add(entity.geometry.end, delta),
          ...(entity.geometry.width !== undefined ? { width: entity.geometry.width } : {}),
        },
      };
    case 'object':
      return {
        ...entity,
        geometry: {
          ...entity.geometry,
          origin: vec.add(entity.geometry.origin, delta),
        },
      };
  }
}
